import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCcw, ChevronDown, ChevronUp, Building2, Mail, Phone, Calendar,
  TrendingDown, AlertTriangle, DollarSign, HelpCircle,
  FileText, ClipboardCheck, MessageSquare, Check, Save, Loader2, Trash2, ArrowRight,
  Upload, Download, ShieldCheck, ShieldAlert, Users, Landmark, User, Briefcase, Send, MapPin,
  FileCheck, Scale, Shield, CalendarClock, FileBarChart, Receipt, Plus, X, History, Activity, FolderOpen,
  Info, TrendingUp, Table2, ExternalLink, Zap, CircleDot, AlertCircle, Lock, Unlock, CheckCircle2, Flag, PhoneCall, Copy, Star
} from 'lucide-react';
import { ccfsLeadsAPI, casesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { ALL_FYS } from '@/lib/ccfsCalcEngine';

// Phase 1 extraction — small presentational components, constants and utility helpers
import { CollapsibleSection } from '@/pages/admin/ccfs/components/CollapsibleSection';
import { NextStepBanner } from '@/pages/admin/ccfs/components/NextStepBanner';
import { TagDropdown } from '@/pages/admin/ccfs/components/TagDropdown';
import { EditSaveField } from '@/pages/admin/ccfs/components/EditSaveField';
import { DiscoveryFlagToggle } from '@/pages/admin/ccfs/components/DiscoveryFlagToggle';
import {
  CASE_TYPES, COMPLEXITIES, PIPELINE_STAGES, PIPELINE_COLORS,
  CASE_TYPE_COLORS, COMPLEXITY_COLORS, STATUS_MAP, INTENT_MAP,
  BS_LINE_ITEMS, PL_LINE_ITEMS,
  DECISION_COLORS, DISPLAY_DECISION_LABEL,
} from '@/pages/admin/ccfs/constants';
// Phase 2a extraction — state-owning BS Analysis panel
import { BSAnalysisPanel } from '@/pages/admin/ccfs/sections/BSAnalysisPanel';
// Phase 2b extraction — state + API-owning Eligibility Engine
import { EligibilityEngine } from '@/pages/admin/ccfs/sections/EligibilityEngine';
// Phase 2c extraction — state + API-owning MCA Filing Index section (heaviest)
import { FilingIndexSection } from '@/pages/admin/ccfs/sections/FilingIndexSection';
import { DirectorOtherCompanies } from '@/pages/admin/ccfs/sections/DirectorOtherCompanies';
import MCAMasterDataPanel from '@/pages/admin/ccfs/MCAMasterDataPanel';
import {
  companyTypeFromCIN, fmt, fmtDatePretty, flagRowClass,
  buildFYRange, buildSuggestedFilingMatrix, getBsFyStartYear,
  deriveFilingMatrixFromFYVerification,
} from '@/pages/admin/ccfs/utils';

const API = process.env.REACT_APP_BACKEND_URL;


// ─── SHAREHOLDING STATUS PANEL ───
const SHARE_CLASS_SUGGESTIONS = [
  'Equity',
  'Preference - Series A',
  'Preference - Series B',
  'Preference - Cumulative',
  'Preference - Non-Cumulative',
  'Preference - Redeemable',
  'Preference - Convertible',
];

function ShareholdingPanel({ lead, onRefresh }) {
  const stored = lead.shareholding || {};
  const isLocked = !!stored.locked;
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlock = async () => {
    if (!window.confirm('Unlock Shareholding? This will allow editing of both BS-date and Today snapshots. Changes will be re-locked on next save.')) return;
    setUnlocking(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ccfs-leads/${lead.id}/shareholding-lock`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ locked: false }),
      });
      if (!res.ok) throw new Error('unlock failed');
      toast.success('Shareholding unlocked');
      onRefresh?.();
    } catch { toast.error('Failed to unlock Shareholding'); }
    finally { setUnlocking(false); }
  };

  // Legacy migration: if only flat `shareholders` exists, seed it as the BS-date snapshot with class=Equity
  const initialBS = stored.as_on_bs_date || {
    date: stored.as_on_date || lead.mca_company_info?.date_of_balance_sheet || '',
    source: (stored.shareholders?.length ? 'MGT-7 / Annual Return' : ''),
    rows: (stored.shareholders || []).map(r => ({
      name: r.name || '', din_pan: r.din_pan || '', share_class: r.share_class || 'Equity', shares: r.shares || 0,
    })),
  };
  const initialToday = stored.as_on_today || { date: '', source: '', rows: [] };

  // Auto-import directors when both snapshots are empty — admin verifies/edits shares before save.
  const hasAnyStoredRows = !!(initialBS.rows?.length || initialToday.rows?.length || stored.shareholders?.length);
  const _allDirectors = (lead.mca_directors || []).filter(d => {
    const status = (d.din_status || '').toLowerCase();
    return !['deactivated', 'disqualified_164', 'surrendered'].includes(status);
  });
  const autoImported = !hasAnyStoredRows && _allDirectors.length > 0;
  if (autoImported) {
    const importedRows = _allDirectors.map(d => ({
      name: d.name || '',
      din_pan: d.din_pan || d.din || d.pan || '',
      share_class: 'Equity',
      shares: 0,
    }));
    initialBS.rows = importedRows;
    initialToday.rows = importedRows.map(r => ({ ...r }));
    if (!initialBS.source) initialBS.source = 'Imported from Directors — please verify';
    if (!initialToday.source) initialToday.source = 'Imported from Directors — please verify';
  }

  const [bsDate, setBsDate] = useState(initialBS.date || '');
  const [bsSource, setBsSource] = useState(initialBS.source || '');
  const [bsRows, setBsRows] = useState(initialBS.rows);
  const [todayDate, setTodayDate] = useState(initialToday.date || '');
  const [todaySource, setTodaySource] = useState(initialToday.source || '');
  const [todayRows, setTodayRows] = useState(initialToday.rows);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(!!(initialBS.rows?.length || initialToday.rows?.length));

  const filingIndex = lead.filing_index || {};
  const impactingFilings = filingIndex.shareholding_impacting_filings || [];

  const addRow = (which) => {
    const newRow = { name: '', din_pan: '', share_class: 'Equity', shares: 0 };
    if (which === 'bs') setBsRows([...bsRows, newRow]);
    else setTodayRows([...todayRows, newRow]);
  };
  const removeRow = (which, i) => {
    if (which === 'bs') setBsRows(bsRows.filter((_, idx) => idx !== i));
    else setTodayRows(todayRows.filter((_, idx) => idx !== i));
  };
  const updateRow = (which, i, field, value) => {
    const rows = which === 'bs' ? [...bsRows] : [...todayRows];
    rows[i] = { ...rows[i], [field]: field === 'shares' ? (parseInt(value) || 0) : value };
    if (which === 'bs') setBsRows(rows); else setTodayRows(rows);
  };

  const copyBsToToday = () => {
    if (todayRows.length && !window.confirm('Replace current "As on Today" rows with BS-date rows?')) return;
    setTodayRows(bsRows.map(r => ({ ...r })));
    if (!todayDate) setTodayDate(new Date().toISOString().slice(0, 10));
    if (!todaySource) setTodaySource('Derived from BS-date — update with post-BS filings');
  };

  // Active directors eligible for pre-fill (exclude deactivated / disqualified / surrendered / past cessation)
  const activeDirectors = (lead.mca_directors || []).filter(d => {
    const status = (d.din_status || 'not_verified').toLowerCase();
    if (['deactivated', 'disqualified_164', 'surrendered'].includes(status)) return false;
    const ces = (d.cessation_date || '').trim();
    if (ces) {
      const cesDate = new Date(ces + 'T00:00:00');
      if (!isNaN(cesDate) && cesDate < new Date()) return false;
    }
    return true;
  });

  const addFromDirector = (which, director) => {
    const rows = which === 'bs' ? bsRows : todayRows;
    const dinPan = director.din_pan || director['din/pan'] || '';
    const newRow = {
      name: director.name || '',
      din_pan: dinPan,
      share_class: 'Equity',
      shares: 0,
    };
    if (which === 'bs') setBsRows([...rows, newRow]);
    else setTodayRows([...rows, newRow]);
  };

  // Per-class subtotals for a snapshot
  const classSubtotals = (rows) => {
    const byClass = {};
    rows.forEach(r => {
      const c = r.share_class || 'Equity';
      byClass[c] = (byClass[c] || 0) + (r.shares || 0);
    });
    return byClass;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ccfs-leads/${lead.id}/shareholding`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          as_on_bs_date: { date: bsDate, source: bsSource, rows: bsRows },
          as_on_today: { date: todayDate, source: todaySource, rows: todayRows },
        }),
      });
      toast.success('Shareholding saved');
      onRefresh?.();
    } catch { toast.error('Failed to save shareholding'); }
    finally { setSaving(false); }
  };

  const DirectorsPrefillDropdown = ({ which, rows }) => {
    const [open, setOpen] = useState(false);
    if (!activeDirectors.length) return null;
    const existingKeys = new Set();
    rows.forEach(r => {
      const k = (r.din_pan || '').trim().toLowerCase() || `name:${(r.name || '').trim().toLowerCase()}`;
      if (k && k !== 'name:') existingKeys.add(k);
    });
    return (
      <div className="relative">
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)}
          className="h-6 text-[10px] gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
          data-testid={`sh-from-directors-${which}-${lead.id}`}
          title="Add director as a shareholder — pre-fills name + DIN/PAN with 0 shares. Enter count from MGT-7.">
          <Users className="w-3 h-3" /> From Directors
        </Button>
        {open && (
          <div className="absolute right-0 top-7 z-20 bg-white border border-purple-200 rounded-lg shadow-lg w-64 max-h-56 overflow-y-auto" data-testid={`sh-dir-dropdown-${which}-${lead.id}`}>
            <div className="px-3 py-1.5 bg-purple-50 border-b border-purple-100">
              <p className="text-[9px] text-purple-600 font-semibold uppercase tracking-wide">Active Directors</p>
            </div>
            {activeDirectors.map((d, i) => {
              const dinPan = d.din_pan || d['din/pan'] || '';
              const key = dinPan.trim().toLowerCase() || `name:${(d.name || '').trim().toLowerCase()}`;
              const already = existingKeys.has(key);
              return (
                <button
                  key={`dir-${i}-${dinPan}`}
                  type="button"
                  disabled={already}
                  onClick={() => { if (already) return; addFromDirector(which, d); setOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 border-b border-slate-100 last:border-0 flex items-center justify-between gap-2 ${
                    already ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'hover:bg-purple-50 cursor-pointer'
                  }`}
                  data-testid={`sh-dir-pick-${which}-${i}-${lead.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-slate-800 truncate">{d.name || '—'}</p>
                    <p className="text-[9px] text-slate-400 font-mono truncate">{dinPan || 'No DIN/PAN'}</p>
                  </div>
                  {already ? (
                    <span className="text-[9px] text-emerald-600 font-semibold shrink-0">✓ Added</span>
                  ) : (
                    <Plus className="w-3 h-3 text-purple-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderSnapshot = (label, which, date, setDate, source, setSource, rows) => {
    const classTotals = classSubtotals(rows);
    const grandTotal = Object.values(classTotals).reduce((a, b) => a + b, 0);
    return (
      <div className="bg-white rounded-lg border border-purple-200 p-3" data-testid={`sh-snap-${which}-${lead.id}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">{label}</p>
          <div className="flex items-center gap-1">
            {which === 'today' && bsRows.length > 0 && (
              <Button size="sm" variant="outline" onClick={copyBsToToday} className="h-6 text-[10px] gap-1 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`sh-copy-bs-to-today-${lead.id}`}>
                <Copy className="w-3 h-3" /> Copy BS → Today
              </Button>
            )}
            <DirectorsPrefillDropdown which={which} rows={rows} />
            <Button size="sm" onClick={() => addRow(which)} className="h-6 text-[10px] gap-1 bg-purple-600 hover:bg-purple-700" data-testid={`sh-add-row-${which}-${lead.id}`}>
              <Plus className="w-3 h-3" /> Add Row
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-0.5">As-on Date</p>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-7 text-[11px]" data-testid={`sh-date-${which}-${lead.id}`} />
          </div>
          <div>
            <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-0.5">Source / Notes</p>
            <Input value={source} onChange={e => setSource(e.target.value)} placeholder={which === 'bs' ? 'e.g. MGT-7 for FY 2023-24' : 'e.g. Post PAS-3 dated 15-Jun-2024'} className="h-7 text-[11px]" data-testid={`sh-source-${which}-${lead.id}`} />
          </div>
        </div>
        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-purple-50 text-purple-700">
                  <th className="px-1.5 py-1 text-left font-semibold">Shareholder Name</th>
                  <th className="px-1.5 py-1 text-left font-semibold">DIN/PAN</th>
                  <th className="px-1.5 py-1 text-left font-semibold">Share Class</th>
                  <th className="px-1.5 py-1 text-right font-semibold">Shares</th>
                  <th className="px-1.5 py-1 text-right font-semibold">% of Class</th>
                  <th className="px-1.5 py-1 w-7"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const cls = r.share_class || 'Equity';
                  const classTot = classTotals[cls] || 0;
                  const pct = classTot > 0 ? ((r.shares || 0) / classTot * 100).toFixed(2) : '0.00';
                  return (
                    <tr key={`sh-${which}-${i}`} className="border-t border-slate-100">
                      <td className="px-0.5 py-0.5"><Input value={r.name} onChange={e => updateRow(which, i, 'name', e.target.value)} className="h-6 text-[11px]" placeholder="Name" /></td>
                      <td className="px-0.5 py-0.5"><Input value={r.din_pan} onChange={e => updateRow(which, i, 'din_pan', e.target.value)} className="h-6 text-[11px]" placeholder="DIN/PAN" /></td>
                      <td className="px-0.5 py-0.5">
                        <input list={`share-classes-${lead.id}`} value={r.share_class || ''} onChange={e => updateRow(which, i, 'share_class', e.target.value)}
                          className="w-full h-6 text-[11px] px-2 rounded border border-slate-200 bg-white" placeholder="Class" data-testid={`sh-class-${which}-${i}-${lead.id}`} />
                      </td>
                      <td className="px-0.5 py-0.5"><Input type="number" value={r.shares || ''} onChange={e => updateRow(which, i, 'shares', e.target.value)} className="h-6 text-[11px] text-right" /></td>
                      <td className="px-1.5 py-0.5 text-right font-medium text-slate-600 tabular-nums">{pct}%</td>
                      <td className="px-0.5 py-0.5"><button onClick={() => removeRow(which, i)} className="text-red-400 hover:text-red-600" data-testid={`sh-remove-${which}-${i}-${lead.id}`}><X className="w-3 h-3" /></button></td>
                    </tr>
                  );
                })}
                {/* Per-class subtotals */}
                {Object.entries(classTotals).map(([cls, tot]) => (
                  <tr key={`subtotal-${which}-${cls}`} className="border-t border-purple-100 bg-purple-50/30 text-purple-700 text-[10px]">
                    <td className="px-1.5 py-1 italic" colSpan={2}>Total — {cls}</td>
                    <td className="px-1.5 py-1 italic"></td>
                    <td className="px-1.5 py-1 text-right tabular-nums font-semibold">{tot.toLocaleString('en-IN')}</td>
                    <td className="px-1.5 py-1 text-right">100%</td>
                    <td></td>
                  </tr>
                ))}
                <tr className="border-t-2 border-purple-200 bg-purple-50 font-bold text-purple-800">
                  <td className="px-1.5 py-1" colSpan={3}>Grand Total (all classes)</td>
                  <td className="px-1.5 py-1 text-right tabular-nums">{grandTotal.toLocaleString('en-IN')}</td>
                  <td className="px-1.5 py-1"></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 italic py-2 text-center">No shareholders added yet — click "Add Row" to start.</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-purple-200 p-4" data-testid={`shareholding-${lead.id}`}>
      <datalist id={`share-classes-${lead.id}`}>
        {SHARE_CLASS_SUGGESTIONS.map(c => <option key={c} value={c} />)}
      </datalist>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <p className="text-[10px] text-purple-500 uppercase tracking-wide font-semibold flex items-center gap-1.5">
          <Table2 className="w-3.5 h-3.5" /> Shareholding Status
          {isLocked && (
            <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold inline-flex items-center gap-0.5" data-testid={`sh-locked-badge-${lead.id}`}>
              <Lock className="w-2.5 h-2.5" /> Locked
            </span>
          )}
          {stored.consent_75_feasible !== undefined && (
            <span className={`ml-2 text-[9px] px-2 py-0.5 rounded-full font-bold ${stored.consent_75_feasible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
              75% Consent (Equity): {stored.consent_75_feasible ? 'Feasible' : 'Not Feasible'}
            </span>
          )}
          <span className="text-[9px] font-normal text-slate-400 normal-case ml-1">
            {bsRows.length} BS-date · {todayRows.length} today
          </span>
        </p>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Auto-imported nudge — appears only until first save */}
          {autoImported && (
            <div className="flex items-start gap-2 px-3 py-2 rounded border border-yellow-300 bg-yellow-50" data-testid={`sh-auto-imported-banner-${lead.id}`}>
              <AlertCircle className="w-3.5 h-3.5 text-yellow-700 shrink-0 mt-0.5" />
              <span className="text-[11px] text-yellow-800">
                <strong>{_allDirectors.length} director{_allDirectors.length === 1 ? '' : 's'} imported from MCA Directors</strong> with 0 shares.
                Add their actual shareholding %, or remove any director who does not hold shares, then Save.
              </span>
            </div>
          )}
          {/* Shareholding Lock Banner */}
          {isLocked && (
            <div className="flex items-center gap-2 px-3 py-2 rounded border border-emerald-200 bg-emerald-50" data-testid={`sh-locked-banner-${lead.id}`}>
              <Lock className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <span className="text-[11px] text-emerald-800 font-medium">
                Shareholding Locked
                {stored.locked_at && (
                  <span className="ml-2 text-emerald-600 font-normal">
                    · saved by {stored.locked_by || 'Admin'} on {new Date(stored.locked_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </span>
              <Button size="sm" variant="outline" onClick={handleUnlock} disabled={unlocking}
                className="ml-auto h-6 text-[10px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid={`sh-unlock-${lead.id}`}>
                {unlocking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                Unlock to Edit
              </Button>
            </div>
          )}

          {impactingFilings.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-[10px] text-amber-700 font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Post-BS filings that may have changed shareholding
              </p>
              {impactingFilings.map((f) => (
                <p key={`${f.form_name}-${f.date_of_filing}`} className="text-[10px] text-amber-600 mt-0.5">
                  {f.form_name} filed on {f.date_of_filing} — apply delta to "As on Today" snapshot.
                </p>
              ))}
            </div>
          )}

          <fieldset disabled={isLocked} className="contents">
          {renderSnapshot('① As on Last Filed BS Date', 'bs', bsDate, setBsDate, bsSource, setBsSource, bsRows)}
          {renderSnapshot('② As on Today', 'today', todayDate, setTodayDate, todaySource, setTodaySource, todayRows)}
          </fieldset>

          {!isLocked && (
            <div className="flex justify-end">
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700" data-testid={`save-shareholding-${lead.id}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Shareholding
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── STRUCTURED CALL NOTES (Journey Stage 8) ───
const CALL_CHECKLIST = [
  { key: 'cessation_narrative', label: 'Cessation narrative confirmed (when & why business stopped)' },
  { key: 'bank_statements', label: 'Bank statements available (confirm period)' },
  { key: 'directors_reachable', label: 'All directors reachable & in agreement' },
  { key: 'shareholder_consent', label: 'Shareholder consent feasible' },
  { key: 'no_pending_demand', label: 'No pending demand from IT / GST / ROC' },
  { key: 'no_litigation', label: 'No pending litigation / arbitration' },
  { key: 'bs_paths_confirmed', label: 'BS closure paths discussed & confirmed with client' },
  { key: 'timeline_agreed', label: 'Client urgency / timeline expectation set' },
  { key: 'related_companies', label: 'Checked for related companies with same directors' },
];

// ─── DISCOVERY SCOPE CHECKPOINTS ───
// Each TICKED checkpoint contributes a scope line in the auto-derived proposal.
const DISCOVERY_CHECKPOINTS = [
  { key: 'it_notice', label: 'Income-Tax notice / scrutiny pending', side: 'CA', scope: 'Reply to Income-Tax notice — drafting & filing' },
  { key: 'gst_notice', label: 'GST notice / show-cause / audit pending', side: 'CA', scope: 'Reply to GST notice / audit response — drafting & filing' },
  { key: 'bank_loan_charge', label: 'Bank loan / charge open on MCA', side: 'CS', scope: 'Satisfaction of Charge (CHG-4) + bank coordination' },
  { key: 'fema_rbi', label: 'FEMA / RBI matter (FDI, ECB, ODI)', side: 'CA', scope: 'FEMA reporting + RBI advisory' },
  { key: 'vc_equity', label: 'VC / Equity investor on cap-table', side: 'CS', scope: 'Investor consent + share transfer compliance' },
  { key: 'pending_adt1', label: 'Auditor appointment (ADT-1) pending', side: 'CS', scope: 'Auditor appointment & ADT-1 filing' },
  { key: 'employee_dues', label: 'Employee PF / ESIC / gratuity dues', side: 'CA', scope: 'Settlement of employee dues + statutory filings' },
  { key: 'vendor_disputes', label: 'Vendor / customer disputes outstanding', side: 'CS', scope: 'Vendor / customer dispute closure support' },
  { key: 'fcra_sector_licence', label: 'FCRA / sector licence (RBI / SEBI / IRDAI)', side: 'CS', scope: 'Sector licence surrender / NOC coordination' },
  { key: 'legal_case', label: 'Legal case in court / tribunal', side: 'ADMIN', scope: 'Lawyer to be assigned offline — out of CA/CS scope' },
];

function StructuredCallNotes({ lead, onSave }) {
  const stored = lead.structured_call_notes || {};
  const [checks, setChecks] = useState(stored.checklist || {});
  const [scopeChecks, setScopeChecks] = useState(stored.scope_checkpoints || {});
  const [summary, setSummary] = useState(stored.summary || '');
  const [callDate, setCallDate] = useState(stored.call_date || '');
  const [callerName, setCallerName] = useState(stored.caller_name || '');
  const [saving, setSaving] = useState(false);

  // Collect flagged items from BS + P&L analysis (read-only carry-over)
  const flaggedItems = (() => {
    const out = [];
    const bsItems = lead.bs_analysis?.items || {};
    const plItems = lead.bs_analysis?.pl_items || {};
    for (const li of BS_LINE_ITEMS) {
      const d = bsItems[li.key];
      if (d?.discovery_flag) out.push({ source: 'BS', label: li.label, flag: d.discovery_flag, notes: d.notes, path: d.closure_path, complexity: d.complexity, amount: d.amount });
    }
    for (const li of PL_LINE_ITEMS) {
      const d = plItems[li.key];
      if (d?.discovery_flag) out.push({ source: 'P&L', label: li.label, flag: d.discovery_flag, notes: d.notes, path: d.closure_path, complexity: d.complexity, amount: d.amount });
    }
    // Sort red first, then amber
    return out.sort((a, b) => (a.flag === 'red' ? 0 : 1) - (b.flag === 'red' ? 0 : 1));
  })();

  const handleSave = async () => {
    setSaving(true);
    await onSave({ checklist: checks, scope_checkpoints: scopeChecks, summary, call_date: callDate, caller_name: callerName });
    setSaving(false);
  };

  const completedCount = CALL_CHECKLIST.filter(item => checks[item.key]).length;

  return (
    <div className="space-y-3">
      {/* Flagged items carry-over from BS/P&L Analysis (read-only) */}
      {flaggedItems.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden" data-testid={`flagged-items-callover-${lead.id}`}>
          <div className="px-3 py-1.5 bg-white border-b border-slate-200 flex items-center gap-2">
            <Flag className="w-3 h-3 text-red-500" fill="currentColor" />
            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">Items flagged from BS / P&amp;L Analysis</p>
            <span className="text-[9px] text-slate-400">Discuss during call — capture answers in Call Summary below</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {flaggedItems.map((f, i) => (
              <li key={`${f.source}-${f.label}-${i}`} className={`px-3 py-1.5 text-[11px] flex items-start gap-2 ${f.flag === 'red' ? 'bg-red-50/50' : 'bg-amber-50/50'}`}>
                <Flag className={`w-3 h-3 mt-0.5 shrink-0 ${f.flag === 'red' ? 'text-red-500' : 'text-amber-500'}`} fill="currentColor" />
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${f.flag === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{f.source}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{f.label}</span>
                    {f.amount && <span className="text-slate-500 tabular-nums">₹{Number(f.amount).toLocaleString('en-IN')}</span>}
                    {f.path && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{f.path}</span>}
                    {f.complexity && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{f.complexity}</span>}
                  </div>
                  {f.notes && <p className="text-slate-600 mt-0.5 text-[10px]">{f.notes}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Call Date</p>
          <Input type="date" value={callDate} onChange={e => setCallDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Caller</p>
          <Input type="text" placeholder="Who made the call" value={callerName} onChange={e => setCallerName(e.target.value)} className="h-8 text-xs" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {CALL_CHECKLIST.map(item => (
          <label key={item.key} className={`flex items-center gap-2 py-1.5 px-2.5 rounded cursor-pointer transition-colors text-xs ${
            checks[item.key] ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-100 hover:bg-slate-100'
          }`}>
            <Checkbox checked={!!checks[item.key]} onCheckedChange={() => setChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))} className="h-3.5 w-3.5" />
            <span className={checks[item.key] ? 'text-emerald-700' : 'text-slate-600'}>{item.label}</span>
          </label>
        ))}
      </div>

      {/* Discovery Scope Checkpoints — each ticked item contributes a scope line to the proposal */}
      <div className="rounded-lg border border-violet-200 bg-violet-50/30 p-3" data-testid={`scope-checkpoints-${lead.id}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wide">Discovery Scope Checkpoints</p>
          <span className="text-[9px] text-violet-600">{Object.values(scopeChecks).filter(Boolean).length} of {DISCOVERY_CHECKPOINTS.length} flagged</span>
        </div>
        <p className="text-[10px] text-violet-600 mb-2 italic">Each ticked item auto-adds a scope line in the Proposal section below.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {DISCOVERY_CHECKPOINTS.map(item => (
            <label key={item.key} className={`flex items-center gap-2 py-1.5 px-2.5 rounded cursor-pointer transition-colors text-xs ${scopeChecks[item.key] ? 'bg-violet-100 border border-violet-300' : 'bg-white border border-violet-100 hover:bg-violet-50'}`}>
              <Checkbox checked={!!scopeChecks[item.key]} onCheckedChange={() => setScopeChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))} className="h-3.5 w-3.5" data-testid={`scope-cp-${item.key}-${lead.id}`} />
              <span className={`text-[9px] font-bold px-1 py-0.5 rounded shrink-0 ${item.side === 'CA' ? 'bg-blue-100 text-blue-700' : item.side === 'CS' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{item.side}</span>
              <span className={scopeChecks[item.key] ? 'text-violet-800 font-medium' : 'text-slate-700'}>{item.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Call Summary / Key Findings</p>
        <textarea
          placeholder="Key observations from the discovery call..."
          value={summary}
          onChange={e => setSummary(e.target.value)}
          rows={3}
          className="w-full text-xs px-3 py-2 rounded border border-slate-200 bg-white resize-y"
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-400">{completedCount}/{CALL_CHECKLIST.length} items discussed</span>
        <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs gap-1 bg-[#1e3a5f] hover:bg-[#2a4f7a]" data-testid={`save-call-notes-${lead.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Notes
        </Button>
      </div>
    </div>
  );
}

// ─── CESSATION DATE OVERRIDE (Journey Stage 9) ───

function CessationOverridePanel({ lead, onDone }) {
  const currentFY = lead.cessation_fy_override || lead.cessation_fy || '';
  const isOverridden = !!lead.cessation_fy_override;
  const [newFY, setNewFY] = useState(currentFY);
  const [reason, setReason] = useState(lead.cessation_override_notes || '');
  const [saving, setSaving] = useState(false);

  // Intent override (Close / Continue) — variant-changing.
  const originalIntent = (lead.quiz_answers?.[2] || '').toString().toLowerCase().trim();
  const currentIntent = (lead.intent_override || originalIntent || '').toString().toLowerCase().trim();
  const [newIntent, setNewIntent] = useState(currentIntent);
  const isIntentOverridden = !!lead.intent_override && lead.intent_override !== originalIntent;

  // Pre-compute variant before vs after for the live "Variant changed" pill
  const _cinUp = (lead.mca_company_info?.cin || lead.cin || '').toString().toUpperCase();
  const _isS8 = _cinUp.includes('NPL') ||
    (lead.mca_company_info?.class_of_company || '').toLowerCase().includes('section 8') ||
    (lead.mca_company_info?.subcategory_of_the_company || lead.mca_company_info?.sub_category || '').toLowerCase().includes('section 8');
  const _variantOf = (intent) => {
    const i = (intent || '').toLowerCase();
    if (['continue', 'regularise', 'regularize', 'dormant'].includes(i)) return 'B';
    if (_isS8) return 'B';
    return 'A';
  };
  const currentVariant = _variantOf(currentIntent);
  const newVariant = _variantOf(newIntent);
  const willChangeVariant = currentVariant !== newVariant;

  // F15: Inconsistency check — cessation FY earlier than last filed BS FY
  const lastBsDate = lead.mca_company_info?.date_of_balance_sheet || '';
  let inconsistent = false;
  let bsFyLabel = '';
  if (lastBsDate && currentFY) {
    const bsYear = parseInt(lastBsDate.toString().slice(0, 4), 10);
    const cessYear = parseInt((currentFY.match(/(\d{4})/) || [])[1] || '0', 10);
    if (bsYear && cessYear && cessYear < bsYear - 1) {
      inconsistent = true;
      bsFyLabel = `FY ${bsYear - 1}-${String(bsYear).slice(-2)}`;
    }
  }

  const leadSaysNoIdea = (lead.quiz_answers?.[1] || '').toString().toLowerCase() === 'noidea';
  const notSetYet = !currentFY;

  const fmtOverrideBadge = () => {
    if (!isOverridden) return null;
    const who = lead.cessation_override_by || 'Admin';
    const when = lead.cessation_override_at ? new Date(lead.cessation_override_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    return `Override: ${lead.cessation_fy || '—'} → ${lead.cessation_fy_override} (by ${who}${when ? ', ' + when : ''})`;
  };

  const fyChanged = newFY && newFY !== currentFY;
  const intentChanged = newIntent && newIntent !== currentIntent;
  const canSave = (fyChanged || intentChanged) && reason.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const payload = { reason };
      if (fyChanged) payload.cessation_fy_override = newFY;
      if (intentChanged) payload.intent_override = newIntent;
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/cessation-override`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        if (intentChanged && willChangeVariant) {
          toast.success(`Variant changed: ${currentVariant} → ${newVariant}`);
        } else if (intentChanged) {
          toast.success(`Intent overridden to "${newIntent}"`);
        } else {
          toast.success(`Cessation FY overridden to ${newFY}`);
        }
        onDone?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save override');
      }
    } catch { toast.error('Failed to save override'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2.5">
      {/* F14: Noidea nudge */}
      {(leadSaysNoIdea || notSetYet) && !isOverridden && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-300 px-3 py-2 flex items-start gap-2" data-testid={`cessation-noidea-banner-${lead.id}`}>
          <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-yellow-800">
            <span className="font-bold">Lead did not know cessation FY.</span> Please confirm and set after Discovery Call — gap rules will be inaccurate until a valid FY is recorded here.
          </p>
        </div>
      )}
      {/* F15: BS-date consistency check */}
      {inconsistent && (
        <div className="rounded-lg bg-orange-50 border border-orange-300 px-3 py-2 flex items-start gap-2" data-testid={`cessation-inconsistency-${lead.id}`}>
          <AlertCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-orange-800">
            <span className="font-bold">Inconsistency:</span> Last filed AOC-4 is for {bsFyLabel}, but cessation is set to {currentFY}. Please verify with supporting evidence.
          </p>
        </div>
      )}

      {/* Current state row */}
      <div className="flex items-center gap-3 text-xs flex-wrap">
        <span className="text-slate-500">Cessation:</span>
        <span className="font-medium text-slate-700">{lead.cessation_fy || '—'}</span>
        {isOverridden && (
          <>
            <ArrowRight className="w-3 h-3 text-amber-500" />
            <span className="font-semibold text-amber-700">{lead.cessation_fy_override}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium border border-amber-200" title={fmtOverrideBadge()} data-testid={`cessation-override-badge-${lead.id}`}>
              {fmtOverrideBadge()}
            </span>
          </>
        )}
        <span className="mx-1 text-slate-300">·</span>
        <span className="text-slate-500">Intent:</span>
        <span className="font-medium text-slate-700 capitalize">{originalIntent || '—'}</span>
        {isIntentOverridden && (
          <>
            <ArrowRight className="w-3 h-3 text-fuchsia-500" />
            <span className="font-semibold text-fuchsia-700 capitalize">{lead.intent_override}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 font-medium border border-fuchsia-200" data-testid={`intent-override-badge-${lead.id}`}>
              Override active
            </span>
          </>
        )}
      </div>
      {isOverridden && lead.cessation_override_notes && (
        <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <span className="font-semibold text-slate-600">Reason on record:</span> {lead.cessation_override_notes}
        </div>
      )}

      {/* Override controls — single combined card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">New Cessation FY</p>
          <select
            value={newFY}
            onChange={e => setNewFY(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white h-8"
            data-testid={`cessation-fy-select-${lead.id}`}
          >
            <option value="">Select FY...</option>
            {ALL_FYS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Client Intent</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setNewIntent('close')}
              className={`flex-1 text-xs px-2 py-1.5 rounded border h-8 transition-colors ${newIntent === 'close' ? 'bg-red-50 border-red-300 text-red-700 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`intent-close-${lead.id}`}
            >Close</button>
            <button
              type="button"
              onClick={() => setNewIntent('continue')}
              className={`flex-1 text-xs px-2 py-1.5 rounded border h-8 transition-colors ${newIntent === 'continue' ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid={`intent-continue-${lead.id}`}
            >Continue</button>
          </div>
        </div>
      </div>
      <div>
        <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Reason for Override <span className="text-red-500">*</span></p>
        <input
          type="text"
          placeholder="e.g., Confirmed by director via call"
          value={reason}
          onChange={e => setReason(e.target.value)}
          className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white h-8"
          data-testid={`cessation-reason-${lead.id}`}
        />
      </div>

      {/* Variant change preview */}
      {intentChanged && willChangeVariant && (
        <div className="rounded-lg bg-fuchsia-50 border border-fuchsia-300 px-3 py-2 flex items-start gap-2" data-testid={`variant-change-preview-${lead.id}`}>
          <AlertCircle className="w-4 h-4 text-fuchsia-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-fuchsia-800">
            <span className="font-bold">Report variant will change:</span> {currentVariant} → {newVariant}
            {newVariant === 'B' ? ' (Compliance Regularisation)' : ' (Closure / Strike-off)'}.
            The detailed report will re-render automatically after save.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !canSave}
          className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700"
          data-testid={`save-cessation-${lead.id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarClock className="w-3 h-3" />} Save Override
        </Button>
      </div>
    </div>
  );
}

// ─── DETAILED REPORT (Journey Stage 10) ───

function DetailedReportPanel({ lead, onDone }) {
  const [report, setReport] = useState(lead.detailed_report || null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/detailed-report/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        setShowPreview(true);
        toast.success('Detailed Report generated');
        onDone?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate report');
      }
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const el = report?.eligibility_assessment;
  const bs = report?.bs_analysis_summary;
  const call = report?.call_findings;
  const filing = report?.filing_summary;
  const scheme = report?.scheme;
  const snap = report?.snapshot;
  const company = report?.company_analysis;
  const sh = report?.shareholding_summary;
  const redFlags = report?.red_flags;
  const observations = report?.observations;
  const route = report?.recommended_route;

  // Resolve Report Variant (A = Closure, B = Regularisation).
  // Variant B triggers when intent is 'continue' OR company is Section 8 (NPL CIN / Section 8 class).
  const _intent = (lead.intent_override || lead.quiz_answers?.[2] || lead.intent || '').toString().toLowerCase().trim();
  const _cinUpper = (lead.mca_company_info?.cin || lead.cin || '').toString().toUpperCase();
  const _classLower = (lead.mca_company_info?.class_of_company || '').toString().toLowerCase();
  const _subcatLower = (lead.mca_company_info?.subcategory_of_the_company || lead.mca_company_info?.sub_category || '').toString().toLowerCase();
  const isSection8 = _cinUpper.includes('NPL') || _classLower.includes('section 8') || _subcatLower.includes('section 8') || _subcatLower.includes('non-profit') || _subcatLower.includes('non profit');
  const isContinueIntent = ['continue', 'regularise', 'regularize', 'dormant'].includes(_intent);
  const reportVariant = (isContinueIntent || isSection8) ? 'B' : 'A';
  const showSection8Callout = isSection8 && ['close', 'strike_off', 'closed'].includes(_intent);
  const isVariantB = reportVariant === 'B';
  const companyType = companyTypeFromCIN(_cinUpper);
  // Inactive DIN count for Page 1 short warning + Page 3 red callout
  const inactiveDinCount = (lead.mca_directors || []).filter(d => {
    const s = (d.din_status || '').toLowerCase();
    return s && s !== 'active' && s !== 'verified';
  }).length;
  // 3-consecutive-FY gap detection from saved matrix (mirrors backend recalc_savings).
  // Priority chain: filing_index.fy_verification.rows → verified_filing_matrix →
  //                 draft_filing_matrix → filing_matrix. The first chain step ensures
  //                 admin's verified FY data drives the report preview, not stale worksheets.
  // Iter 89: stale `not_required:true` from a prior strike-off save must be IGNORED
  //          when current intent is continue/regularise/dormant OR Section 8.
  const _deriveMatrix = (() => {
    const fiRows = lead.filing_index?.fy_verification?.rows || [];
    if (fiRows.length) {
      const isS8 = !!lead?.cin_info?.is_section_8 || /NPL/i.test(lead?.mca_company_info?.cin || lead?.cin || '');
      const effIntent = (lead.intent_override || lead.quiz_answers?.[2] || '').toString().toLowerCase().trim();
      const honorNotReq = !isS8 && (effIntent === 'close' || effIntent === 'strike_off');
      const m = {};
      for (const r of fiRows) {
        if (!r?.fy) continue;
        const nr = !!r.not_required && honorNotReq;
        m[r.fy] = {
          fs: !!(r.locked || nr || r.aoc4 === 'filed'),
          ar: !!(r.locked || nr || r.mgt7 === 'filed'),
        };
      }
      return m;
    }
    return lead.verified_filing_matrix || lead.draft_filing_matrix || lead.filing_matrix || {};
  });
  const _matrixForGap = _deriveMatrix();
  const _matrixFys = Object.keys(_matrixForGap).map(k => parseInt(String(k).split('-')[0], 10)).filter(n => !isNaN(n)).sort((a, b) => a - b);
  let threeYearGapTriggered = false;
  {
    let run = 0;
    for (const sy of _matrixFys) {
      const e = _matrixForGap[`${sy}-${String(sy + 1).slice(-2)}`] || {};
      if (!e.fs || !e.ar) {
        run++;
        if (run >= 3) { threeYearGapTriggered = true; break; }
      } else {
        run = 0;
      }
    }
  }

  const classColors = {
    full_benefit: 'bg-emerald-100 text-emerald-700',
    partial_benefit: 'bg-blue-100 text-blue-700',
    fee_only: 'bg-amber-100 text-amber-700',
    not_eligible: 'bg-red-100 text-red-600',
    assessment_pending: 'bg-slate-100 text-slate-500',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {report && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
              v{report.version} — {report.generated_at?.slice(0, 10)}
            </span>
          )}
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isVariantB ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}
            title={isVariantB ? 'Variant B — Compliance Regularisation' : 'Variant A — Closure / Strike-off'}
            data-testid={`report-variant-badge-${lead.id}`}
          >
            {isVariantB ? 'Variant B · Regularisation' : 'Variant A · Closure'}
            {isSection8 && ' · Section 8'}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={generateReport}
            disabled={loading}
            className="h-7 text-xs gap-1 bg-violet-600 hover:bg-violet-700"
            data-testid={`gen-detailed-report-${lead.id}`}
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileBarChart className="w-3 h-3" />}
            {report ? 'Re-generate' : 'Generate Detailed Report'}
          </Button>
          {report && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              className="h-7 text-xs gap-1 border-violet-300 text-violet-700"
              data-testid={`toggle-detailed-preview-${lead.id}`}
            >
              {showPreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Preview
            </Button>
          )}
          {report && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('ratnatraya_token');
                  const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/detailed-report/pdf`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (res.ok) {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Detailed_Report_${(lead.company_name || 'Company').replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 40)}.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } else {
                    toast.error('Failed to download PDF');
                  }
                } catch { toast.error('Failed to download PDF'); }
              }}
              className="h-7 text-xs gap-1 border-violet-300 text-violet-700"
              data-testid={`download-detailed-pdf-${lead.id}`}
            >
              <Download className="w-3 h-3" /> PDF
            </Button>
          )}
          {/* Iter 91 — Internal Case File PDF for CA/CS */}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                const token = localStorage.getItem('ratnatraya_token');
                const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/case-file/pdf`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Case_File_${(lead.company_name || 'Company').replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 40)}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Internal Case File downloaded');
                } else {
                  toast.error('Failed to generate Case File');
                }
              } catch { toast.error('Failed to generate Case File'); }
            }}
            className="h-7 text-xs gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
            title="For internal CA/CS use only — never share with client"
            data-testid={`download-case-file-${lead.id}`}
          >
            <Download className="w-3 h-3" /> Case File (Internal)
          </Button>
        </div>
      </div>

      {showPreview && report && (
        <div className="space-y-3 animate-in fade-in duration-200" data-testid={`detailed-report-preview-${lead.id}`}>
          {/* ─── R-PACE Compliance Gap Resolution & Exit Readiness Report ─── */}
          {/* Document header — calm, no gradients */}
          <div className="bg-white rounded-lg border-t-2 border-teal-600 border border-slate-200 px-4 py-3">
            <p className="text-[9px] uppercase tracking-[0.3em] text-teal-700 font-bold">R · P · A · C · E</p>
            <p className="text-base font-bold text-slate-800 mt-1" data-testid={`report-title-${lead.id}`}>
              {isVariantB ? 'Compliance Regularisation Report' : 'Compliance Gap Resolution & Exit Readiness Report'}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">Prepared under the CCFS 2026 Scheme notified by MCA, Government of India</p>
            <p className="text-[11px] text-slate-500">Window open from 15 April 2026 to 15 July 2026</p>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div><span className="text-slate-400">Prepared For</span> <span className="text-slate-800 font-semibold ml-1">{(snap?.company_name || lead.company_name || '—').toUpperCase()}</span></div>
              <div><span className="text-slate-400">CIN</span> <span className="text-slate-800 font-semibold ml-1">{snap?.cin || lead.cin || '—'}</span></div>
              <div><span className="text-slate-400">Company Type</span> <span className="text-slate-800 font-semibold ml-1" data-testid={`report-company-type-${lead.id}`}>{companyType}</span></div>
              <div><span className="text-slate-400">Prepared On</span> <span className="text-slate-800 font-semibold ml-1">{report.generated_at?.slice(0, 10) || '—'}</span></div>
              <div><span className="text-slate-400">Prepared By</span> <span className="text-slate-800 font-semibold ml-1">Ratnatrraya Consulting LLP</span></div>
              <div className="sm:col-span-2"><span className="text-slate-400">Platform Support</span> <span className="text-slate-800 font-semibold ml-1">R-PACE (Readiness Platform for Accelerated Compliance Execution)</span></div>
            </div>
          </div>

          {/* PAGE 1 — Recommendation Card */}
          <div className="bg-teal-50 rounded-lg border-t-2 border-teal-600 border border-teal-200 px-4 py-3" data-testid={`report-summary-card-${lead.id}`}>
            <p className="text-sm font-bold text-teal-800">
              {isVariantB ? 'Compliance Regularisation Summary' : 'Closure Readiness Summary'}
            </p>
            <p className="text-[11px] text-slate-700 mt-2 leading-relaxed">
              {isVariantB
                ? "Based on the available MCA filing position and the company's stated intent to continue operations, the company appears suitable for compliance regularisation under the CCFS 2026 window, subject to final validation of director, financial, and operational confirmations."
                : 'Based on the available MCA filing position and current compliance status, the company appears broadly eligible for compliance regularisation and company closure preparation under the CCFS 2026 window, subject to final validation of director, financial, and operational confirmations.'}
            </p>
          </div>

          {/* PAGE 1 — 6 Summary Cards (2 rows × 3) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              ['Recommended Path', isVariantB ? 'Compliance regularisation under the CCFS 2026 window' : (route || 'Strike-off preparation and filing under the CCFS 2026 window'), false],
              ['Estimated Savings', filing?.estimated_savings ? `${fmt(filing.estimated_savings)} (estimated late-fee waiver across ${filing.pending_fy_count || 0} pending form${(filing.pending_fy_count || 0) === 1 ? '' : 's'})` : 'No estimated late-fee savings', true],
              ['Estimated Timeline', 'Approximately 45–60 days post engagement', false],
              ['Filing Position', `${filing?.pending_fy_count || 0} annual filing gap${(filing?.pending_fy_count || 0) === 1 ? '' : 's'} identified`, false],
              ['Director Position', (() => {
                if (inactiveDinCount === 0) return 'All director DINs appear active';
                const w = inactiveDinCount === 1 ? 'director' : 'directors';
                const has = inactiveDinCount === 1 ? 'has' : 'have';
                return `${inactiveDinCount} ${w} DIN ${has} a disqualification flag — barred from new appointments and current company cannot file forms until reactivation`;
              })(), false],
              ['Shareholder Position', isVariantB
                ? 'Routine board / shareholder approvals; no 75% consent threshold'
                : (sh?.consent_75_feasible === true ? '75% shareholder consent required and appears achievable' : sh?.consent_75_feasible === false ? '75% shareholder consent required; coordination needed' : '75% shareholder consent required; to be confirmed'), false],
            ].map(([label, value, teal]) => (
              <div key={label} className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg px-3 py-2.5">
                <p className="text-[8.5px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
                <p className={`text-[11px] mt-1 font-semibold leading-snug ${teal ? 'text-teal-700' : 'text-slate-700'}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* PAGE 1 — Recommended Next Step strip */}
          <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-2.5 flex items-center gap-3">
            <p className="text-[11px] font-bold text-slate-800 shrink-0">Recommended Next Step</p>
            <p className="text-[11px] text-slate-700">
              {isVariantB ? 'Validation call and compliance regularisation discussion' : 'Validation call and closure planning discussion'}
            </p>
          </div>

          {/* PAGE 1 — Section 8 close-intent callout */}
          {showSection8Callout && (
            <div className="bg-red-50 border-l-2 border-red-600 rounded-r px-3 py-3" data-testid={`section8-callout-${lead.id}`}>
              <p className="text-[11px] font-bold text-red-800 mb-1">Important — Section 8 Company</p>
              <p className="text-[11px] text-slate-700 leading-relaxed">
                Strike-off under STK-2 / CCFS 2026 is not available to Section 8 companies; closure must be pursued via the RoC surrender of licence route under Section 8(6) of the Companies Act, 2013. This report focuses on the compliance regularisation path that must precede or accompany any such surrender application.
              </p>
            </div>
          )}

          {/* PAGE 2 — Why This Window Matters for the Company and Its Stakeholders */}
          <div className="pt-3">
            <p className="text-sm font-bold text-slate-800 border-b-2 border-teal-600 inline-block pb-0.5 mb-3">Why This Window Matters for the Company and Its Stakeholders</p>
            <p className="text-[11px] text-slate-700 leading-relaxed mb-3">
              The CCFS 2026 Scheme introduced by MCA, Government of India provides a limited-period opportunity for companies with pending annual filings to regularise compliance, reduce filing burden, and evaluate structured closure options at significantly lower cost.
            </p>
            <p className="text-[10px] font-bold text-teal-800 mb-2">Why Action During CCFS 2026 Is Important</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                    <th className="text-left px-3 py-2 font-semibold w-2/5">Area</th>
                    <th className="text-left px-3 py-2 font-semibold">Practical Impact During Current Window</th>
                  </tr>
                </thead>
                <tbody>
                  {(isVariantB ? [
                    ['One-Time Compliance Opportunity', 'Pending annual filings can be completed under a special one-time relaxation window'],
                    ['90% Reduction in Additional Filing Fees', 'Only 10% of applicable additional filing fees are payable under the scheme'],
                    ['Immunity From Certain Penalty Proceedings', 'Scheme provides immunity in eligible delayed filing situations subject to conditions'],
                    ['Avoid Future ROC Enforcement Action', 'MCA has stated that ROC action may be initiated against continuing defaulters after closure of the scheme'],
                    ['Reduced Director Risk under Section 164(2)', 'If annual filings stay overdue for 3 consecutive financial years, every director is automatically disqualified for 5 years — barred from new company / LLP appointments and forced to vacate office in the defaulting company. Continued non-compliance also flags the DIN as deactivated, blocking future MCA filings until reactivation.'],
                    ['Lower Risk of Multi-Department Notices', 'Delayed compliance situations may increase exposure to notices or follow-up from MCA, Income Tax, GST, and related authorities'],
                    ['Restore Active Compliance Standing', "Regularised filings restore the company's standing on the MCA portal and enable smooth ongoing operations"],
                  ] : [
                    ['One-Time Compliance Opportunity', 'Pending annual filings can be completed under a special one-time relaxation window'],
                    ['90% Reduction in Additional Filing Fees', 'Only 10% of applicable additional filing fees are payable under the scheme'],
                    ['Reduced Closure Filing Fees', 'Company closure filing fees under STK-2 are payable at only 25% of normal fees'],
                    ['Immunity From Certain Penalty Proceedings', 'Scheme provides immunity in eligible delayed filing situations subject to conditions'],
                    ['Avoid Future ROC Enforcement Action', 'MCA has stated that ROC action may be initiated against continuing defaulters after closure of the scheme'],
                    ['Reduced Director Risk under Section 164(2)', 'If annual filings stay overdue for 3 consecutive financial years, every director is automatically disqualified for 5 years — barred from new company / LLP appointments and forced to vacate office in the defaulting company. Continued non-compliance also flags the DIN as deactivated, blocking future MCA filings until reactivation.'],
                    ['Lower Risk of Multi-Department Notices', 'Delayed closure situations may increase exposure to notices or follow-up from MCA, Income Tax, GST, and related authorities'],
                    ['Avoid Ongoing Annual Compliance Costs', 'Continued company existence may require recurring audit, filing, accounting, and compliance expenses each year'],
                  ]).map(r => (
                    <tr key={r[0]} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-700 align-top">{r[0]}</td>
                      <td className="px-3 py-2 text-slate-700">{r[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-teal-50 rounded-lg border-t-2 border-teal-600 border border-teal-200 px-4 py-3 mt-4">
              <p className="text-[11px] font-bold text-teal-800 mb-1">Current Opportunity Window</p>
              <p className="text-[11px] font-bold text-teal-700">Scheme open from 15 April 2026 to 15 July 2026</p>
              <p className="text-[11px] text-slate-700 mt-2">
                {isVariantB
                  ? 'The current window is designed to help non-compliant companies:'
                  : 'The current window is designed to help inactive or non-compliant companies either:'}
              </p>
              <ul className="mt-1.5 space-y-1">
                {(isVariantB
                  ? ['complete pending annual filings,', 'restore active compliance standing on the MCA portal,', 'and resume ongoing operations without elevated late-fee burden.']
                  : ['complete pending annual filings,', 'move to dormant status,', 'or evaluate structured company closure with lower compliance cost.']
                ).map(item => (
                  <li key={item} className="flex items-start gap-2 text-[11px] text-slate-700">
                    <span className="text-teal-600 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* PAGE 3 — Company and Filing Position */}
          <div className="pt-3">
            <p className="text-sm font-bold text-slate-800 border-b-2 border-teal-600 inline-block pb-0.5 mb-3">Company and Filing Position</p>
            <p className="text-[10px] font-bold text-teal-800 mb-2">MCA Master Data</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2">
                {(() => {
                  const mci = lead.mca_company_info || {};
                  const val = (...keys) => {
                    for (const k of keys) {
                      const v = mci[k];
                      if (v && v !== '-' && v !== '—') return String(v);
                    }
                    return '—';
                  };
                  const cap = (v) => !v || v === '-' || v === '—' ? '—' : (String(v).startsWith('₹') ? String(v) : `₹ ${String(v).trim()}`);
                  const listed = (v) => {
                    if (!v || v === '-' || v === '—') return '—';
                    const s = String(v).trim().toLowerCase();
                    if (['yes', 'y', 'true', 'listed'].includes(s)) return 'Listed';
                    if (['no', 'n', 'false', 'unlisted'].includes(s)) return 'Unlisted';
                    return String(v).trim();
                  };
                  // Field order alternates Left, Right, Left, Right... in the grid
                  const fields = [
                    ['Company Name', (snap?.company_name || lead.company_name || mci.company_name || '—').toUpperCase()],
                    ['Date of Incorporation', fmtDatePretty(val('date_of_incorporation'))],
                    ['CIN', val('cin') !== '—' ? val('cin') : (snap?.cin || lead.cin || '—')],
                    ['Last Filed Balance Sheet', fmtDatePretty(val('date_of_balance_sheet'))],
                    ['Company Type', companyType],
                    ['Last AGM Held On', fmtDatePretty(val('date_of_last_agm'))],
                    ['Company Status', val('company_status') !== '—' ? val('company_status') : (snap?.company_status || 'Active')],
                    ['Authorised Capital', cap(mci.authorised_capital_rs || mci.authorised_capital)],
                    ['Class', val('class_of_company')],
                    ['Paid-up Capital', cap(mci.paid_up_capital_rs || mci.paid_up_capital)],
                    ['Category', val('category_of_company', 'category')],
                    ['ROC', val('roc_name', 'roc_name_and_office', 'roc_code', 'roc')],
                    ['Sub-category', val('subcategory_of_the_company', 'sub_category')],
                    ['Regional Director', val('rd_name_and_region')],
                    ['Listed Status', listed(mci.listed_in_stock_exchanges_y_n || mci.listed_status || mci.whether_listed)],
                    ['Registered Office Address', val('registered_address', 'registered_office_address')],
                    ['Registration Number', val('registration_number')],
                  ];
                  return fields.map(([label, value], i) => (
                    <div key={label} className={`px-3 py-2 border-slate-100 ${i % 2 === 0 ? 'sm:border-r' : ''} ${i >= 2 ? 'border-t' : ''}`}>
                      <p className="text-[8.5px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
                      <p className="text-[11px] font-semibold text-slate-800 mt-0.5">{value}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
            <p className="text-[10px] font-bold text-slate-700 mt-4 mb-2">Directors and DIN Status</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                    <th className="text-left px-3 py-2 font-semibold">Name</th>
                    <th className="text-left px-3 py-2 font-semibold">DIN/PAN</th>
                    <th className="text-left px-3 py-2 font-semibold">Designation</th>
                    <th className="text-left px-3 py-2 font-semibold">Appointed</th>
                    <th className="text-left px-3 py-2 font-semibold">DIN Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(lead.mca_directors || []).map((d, i) => {
                    const statusRaw = (d.din_status || 'active').toLowerCase();
                    const isInactive = statusRaw !== 'active' && statusRaw !== 'verified';
                    return (
                      <tr key={`d-${i}-${d.din || d.name}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-700">{d.name || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{d.din_pan || d.din || d.pan || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{d.designation || 'Director'}</td>
                        <td className="px-3 py-2 text-slate-700">{fmtDatePretty(d.appointment_date || d.date_of_appointment)}</td>
                        <td className={`px-3 py-2 capitalize ${isInactive ? 'text-red-600 font-bold bg-red-50' : 'text-slate-700'}`}>
                          {statusRaw.replace(/_/g, ' ')}
                        </td>
                      </tr>
                    );
                  })}
                  {(!lead.mca_directors || lead.mca_directors.length === 0) && (
                    <tr><td colSpan="5" className="px-3 py-3 text-center text-slate-400">Director details to be confirmed.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {inactiveDinCount > 0 && (
              <div className="bg-red-50 border-l-2 border-red-600 rounded-r px-3 py-3 mt-3" data-testid={`din-disqualified-callout-${lead.id}`}>
                <p className="text-[11px] font-bold text-red-800 mb-1">Disqualified DIN — Immediate Attention Required</p>
                <p className="text-[11px] text-slate-700 leading-relaxed">
                  {inactiveDinCount === 1 ? 'One' : inactiveDinCount} of the {(lead.mca_directors || []).length} {(lead.mca_directors || []).length === 1 ? 'director' : 'directors'} currently {inactiveDinCount === 1 ? 'has' : 'have'} a non-active DIN. The affected director {inactiveDinCount === 1 ? 'is' : 'are'} barred from being appointed as a director of any company or designated partner of any LLP, and the current company cannot validly file MCA forms until the DIN is reactivated. Reactivation must be completed before any pending compliance filing can proceed.
                </p>
              </div>
            )}
            <p className="text-[10px] font-bold text-slate-700 mt-4 mb-2">Annual Filing Position</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                    <th className="text-left px-3 py-2 font-semibold">Financial Year</th>
                    <th className="text-left px-3 py-2 font-semibold">AOC-4</th>
                    <th className="text-left px-3 py-2 font-semibold">MGT-7</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Reuse the same priority chain as _matrixForGap above so the
                    // table row counts agree with the preview's other filing computations.
                    const matrix = _matrixForGap;
                    const fys = Object.keys(matrix).sort();
                    // Date-based due-date check, day-precise (mirrors backend _calc_savings).
                    // AOC-4 due 30 Oct of FY+1; MGT-7 due 29 Nov of FY+1.
                    const today = new Date();
                    const tY = today.getFullYear();
                    const tM = today.getMonth() + 1;
                    const tD = today.getDate();
                    const fyStart = (k) => parseInt(String(k).split('-')[0], 10);
                    const fsDue = (k) => { const dy = fyStart(k) + 1; if (isNaN(dy)) return true; if (tY > dy) return true; if (tY < dy) return false; return (tM > 10) || (tM === 10 && tD >= 30); };
                    const arDue = (k) => { const dy = fyStart(k) + 1; if (isNaN(dy)) return true; if (tY > dy) return true; if (tY < dy) return false; return (tM > 11) || (tM === 11 && tD >= 29); };
                    return fys.map(fy => {
                      const e = matrix[fy] || {};
                      const fsCell = e.fs ? 'Filed' : (fsDue(fy) ? 'Not Filed' : 'Not Yet Due');
                      const arCell = e.ar ? 'Filed' : (arDue(fy) ? 'Not Filed' : 'Not Yet Due');
                      const fsClass = e.fs ? 'text-emerald-700' : (fsDue(fy) ? 'text-red-600 font-semibold' : 'text-slate-400 italic');
                      const arClass = e.ar ? 'text-emerald-700' : (arDue(fy) ? 'text-red-600 font-semibold' : 'text-slate-400 italic');
                      return (
                        <tr key={fy} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-semibold text-slate-700">FY {fy}</td>
                          <td className={`px-3 py-2 ${fsClass}`}>{fsCell}</td>
                          <td className={`px-3 py-2 ${arClass}`}>{arCell}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
            {filing?.pending_fy_count > 0 && (
              <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-2.5 mt-3">
                <p className="text-[11px] text-slate-700 leading-relaxed">
                  {filing.pending_fy_count} annual filing gap{filing.pending_fy_count === 1 ? ' has' : 's have'} been identified. {filing.pending_fy_count === 1 ? 'This filing' : 'These filings'} must be {isVariantB ? 'regularised under the CCFS 2026 window to restore active compliance standing.' : 'regularised before the company closure application can be completed.'}
                </p>
              </div>
            )}
            {threeYearGapTriggered && (
              <div className="bg-red-50 border-l-2 border-red-600 rounded-r px-3 py-3 mt-3" data-testid={`section-164-callout-${lead.id}`}>
                <p className="text-[11px] font-bold text-red-800 mb-1">Section 164(2) Risk — 3 Consecutive Years of Non-Filing</p>
                <p className="text-[11px] text-slate-700 leading-relaxed">
                  If a company fails to file financial statements / annual returns for 3 consecutive financial years, every director is automatically disqualified for 5 years under Section 164(2) of the Companies Act, 2013. Disqualified directors are barred from being appointed as a director in any other company, and they automatically vacate office in the defaulting company. The DIN must be reactivated before any pending filings can be completed.
                </p>
              </div>
            )}

            {/* Iter 90 — Director Other Companies → Section 167(1) impact map */}
            {(lead.director_other_companies || []).length > 0 && (
              <div className="mt-4" data-testid={`detailed-report-director-other-companies-${lead.id}`}>
                <p className="text-[11px] font-bold text-slate-700 mb-2">Director Disqualification Risk — Section 167(1) Impact Map</p>
                {(lead.director_other_companies || []).map((rec) => {
                  const rs = rec.risk_summary || {};
                  const impact = rs.section_167_1_impact || 0;
                  return (
                    <div key={rec.din} className="mb-3 border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-[11px] font-bold text-slate-900">{rec.name || '—'} <span className="text-[10px] font-mono text-slate-500 font-normal">· DIN {rec.din}</span></p>
                          <p className="text-[10px] text-slate-600">{rs.active_companies || 0} active · {rs.striking_off || 0} strike-off · {rs.ceased || 0} ceased{rs.active_llps || rs.ceased_llps ? ` · ${rs.active_llps || 0}/${rs.ceased_llps || 0} LLP active/ceased` : ''}</p>
                        </div>
                        <p className={`text-[10px] font-semibold px-2 py-1 rounded ${impact > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          Section 167(1) impact: {impact} mandate{impact === 1 ? '' : 's'}
                        </p>
                      </div>
                      {rec.companies?.length > 0 && (
                        <table className="w-full text-[10px]">
                          <thead className="bg-white text-slate-500 text-[9px] uppercase border-b border-slate-200">
                            <tr>
                              <th className="text-left px-2.5 py-1.5">CIN</th>
                              <th className="text-left px-2.5 py-1.5">Company</th>
                              <th className="text-left px-2.5 py-1.5">Status</th>
                              <th className="text-left px-2.5 py-1.5">Role</th>
                              <th className="text-left px-2.5 py-1.5">Appointed</th>
                              <th className="text-left px-2.5 py-1.5">Ceased</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rec.companies.map((c, i) => (
                              <tr key={`${c.cin}-${i}`} className={`border-t border-slate-100 ${c.is_current_case ? 'bg-indigo-50/40' : ''}`}>
                                <td className="px-2.5 py-1.5 font-mono text-slate-700">
                                  {c.cin}
                                  {c.is_current_case && <span className="ml-1 text-[8px] font-semibold text-indigo-700 bg-indigo-100 px-1 py-0.5 rounded">Current</span>}
                                  {c.suspected_3yr_non_filing && <span className="ml-1 text-[8px] font-semibold text-rose-700 bg-rose-100 px-1 py-0.5 rounded">3-yr gap?</span>}
                                </td>
                                <td className="px-2.5 py-1.5 text-slate-800">{c.name || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{c.status || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{c.designation || '—'}{c.category ? ` · ${c.category}` : ''}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{c.appointment_original || c.appointment_current || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{c.cessation || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {rec.llps?.length > 0 && (
                        <table className="w-full text-[10px] border-t-2 border-slate-200">
                          <thead className="bg-white text-slate-500 text-[9px] uppercase border-b border-slate-200">
                            <tr>
                              <th className="text-left px-2.5 py-1.5">LLPIN</th>
                              <th className="text-left px-2.5 py-1.5">LLP</th>
                              <th className="text-left px-2.5 py-1.5">Status</th>
                              <th className="text-left px-2.5 py-1.5">Role</th>
                              <th className="text-left px-2.5 py-1.5">Appointed</th>
                              <th className="text-left px-2.5 py-1.5">Ceased</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rec.llps.map((lp, i) => (
                              <tr key={`${lp.llpin}-${i}`} className="border-t border-slate-100">
                                <td className="px-2.5 py-1.5 font-mono text-slate-700">
                                  {lp.llpin}
                                  {lp.suspected_3yr_non_filing && <span className="ml-1 text-[8px] font-semibold text-rose-700 bg-rose-100 px-1 py-0.5 rounded">3-yr gap?</span>}
                                </td>
                                <td className="px-2.5 py-1.5 text-slate-800">{lp.name || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{lp.status || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{lp.designation || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{lp.appointment_original || lp.appointment_current || '—'}</td>
                                <td className="px-2.5 py-1.5 text-slate-600">{lp.cessation || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* PAGE 4 — Shareholding and Financial Summary */}
          <div className="pt-3">
            <p className="text-sm font-bold text-slate-800 border-b-2 border-teal-600 inline-block pb-0.5 mb-3">Shareholding and Financial Summary</p>
            <p className="text-[10px] font-bold text-slate-700 mb-2">Current Shareholding Position</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                    <th className="text-left px-3 py-2 font-semibold">Shareholder</th>
                    <th className="text-left px-3 py-2 font-semibold">Shares</th>
                    <th className="text-left px-3 py-2 font-semibold">% Holding</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const todayRows = lead.shareholding?.as_on_today?.rows || lead.shareholding?.as_on_bs_date?.rows || lead.shareholding?.shareholders || [];
                    const total = todayRows.reduce((s, r) => s + (r.shares || 0), 0) || 1;
                    if (todayRows.length === 0) return <tr><td colSpan="3" className="px-3 py-3 text-center text-slate-400">Shareholding details to be confirmed during validation.</td></tr>;
                    return todayRows.map((r, i) => (
                      <tr key={`sh-${i}-${r.name}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-700">{r.name || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{(r.shares || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-slate-700">{((r.shares || 0) / total * 100).toFixed(2)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-2.5 mt-3">
              <p className="text-[11px] text-slate-700 leading-relaxed">
                {isVariantB
                  ? 'Shareholding position is documented for record. No special shareholder consent threshold applies for the compliance regularisation route; routine board and shareholder approvals will be coordinated during execution.'
                  : 'Based on the current shareholding position, 75% shareholder approval appears feasible. This means the required consent for company closure can be coordinated through a structured approval process.'}
              </p>
            </div>
            <p className="text-[10px] font-bold text-slate-700 mt-4 mb-1">Condensed Financial Position</p>
            <p className="text-[10px] italic text-slate-500 mb-2">(Based on latest available filed financial statements)</p>
            {(() => {
              const pl = lead.aoc4_parsed?.pl_summary || {};
              const bsItems = lead.aoc4_parsed?.bs_items || {};
              const aoc4Src = lead.aoc4_parsed?.aoc4_source || {};
              const bsOver = lead.bs_analysis?.items || {};
              const plOver = lead.bs_analysis?.pl_items || {};
              const amt = (key) => {
                const v = bsItems[key]?.amount || bsOver[key]?.amount || 0;
                return Number(v) || 0;
              };
              // P&L override-aware reader — mirrors backend PDF logic at
              // generate_detailed_report_pdf.py:1044-1053. Prefers admin's manually
              // entered / auto-computed totals in bs_analysis.pl_items over the
              // AOC-4 parsed pl_summary so Section 8 / dormant leads (where parser
              // returns mostly zeros) still display correctly in the on-screen preview.
              const plAmt = (key) => {
                const ovr = plOver[key]?.amount;
                if (ovr !== undefined && ovr !== null && ovr !== '' && ovr !== '0' && ovr !== 0) {
                  const n = parseFloat(ovr);
                  if (!isNaN(n)) return n;
                }
                return Number(pl[key]) || 0;
              };
              const finFmt = (v) => !v || Number(v) === 0 ? '—' : Number(v) < 0 ? `Loss of ${fmt(Math.abs(v))}` : fmt(v);

              // Detect "no operations" case
              const incorpStr = lead.mca_company_info?.date_of_incorporation || '';
              const parseYM = (s) => { try { const p = s.replace(/-/g, '/').split('/'); if (p.length === 3 && p[2].length === 4) return [+p[2], +p[1]]; if (s[4] === '-') return [+s.slice(0,4), +s.slice(5,7)]; } catch { /* noop */ } return [null, null]; };
              const [iyr, imo] = parseYM(incorpStr);
              const incorpFyStart = iyr ? (imo >= 4 ? iyr : iyr - 1) : null;
              const cessStr = lead.bs_analysis?.cessation_fy_override || lead.cessation_fy || '';
              const cessFyStart = cessStr ? parseInt(String(cessStr).replace('FY', '').trim().split('-')[0], 10) : null;
              const hasAoc4 = Object.keys(bsItems).length > 0 || Object.keys(pl).length > 0;
              // hasBsData also factors in admin's manually entered P&L line items
              // so the "no operations" placeholder doesn't suppress a legitimate
              // hand-entered Condensed Financial Position block.
              const hasBsData = Object.keys(bsOver).length > 0
                || Object.values(plOver).some(v => v?.amount && String(v.amount) !== '0');
              const noOps = cessFyStart && incorpFyStart && cessFyStart === incorpFyStart && !hasAoc4 && !hasBsData;

              if (noOps) {
                return (
                  <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-3">
                    <p className="text-[11px] font-bold text-teal-800 mb-2">
                      {isVariantB ? 'Note: The company did not commence operations within the first year of incorporation.' : 'Ground: The company did not commence operations within the first year of incorporation.'}
                    </p>
                    <p className="text-[11px] text-slate-700 leading-relaxed">
                      {isVariantB
                        ? 'Accordingly, no financial statements were required or filed under the MCA records for that period. Regularisation under the CCFS 2026 window can proceed for subsequent FYs where annual filings are pending.'
                        : 'Accordingly, no financial statements were required or filed under the MCA records. The closure application can proceed on the basis of this ground, subject to director confirmations and the applicable closure documentation under the CCFS 2026 window.'}
                    </p>
                  </div>
                );
              }

              const cur_liab = amt('short_term_borrowings') + amt('trade_payables') + amt('other_current_liab') + amt('short_term_provisions');
              const totalEqLiab = Number(aoc4Src.total_equity_liabilities) || 0;
              const loansAdv = amt('long_term_loans') + amt('short_term_loans');
              const totalAssets = Number(aoc4Src.total_assets) || 0;
              const revOps = plAmt('revenue_operations');
              const otherInc = plAmt('other_income');
              const totalInc = plAmt('total_revenue') || (revOps + otherInc);
              const totalExp = plAmt('total_expenses');
              const netPL = plAmt('net_profit_loss') || plAmt('profit_before_tax');

              const bsRows = [
                ['Equity Share Capital', finFmt(amt('share_capital'))],
                ['Reserves & Surplus', finFmt(amt('reserves'))],
                ['Current Liabilities', finFmt(cur_liab)],
                ['Total Equity & Liabilities', finFmt(totalEqLiab)],
              ];
              const assetRows = [
                ['Cash & Bank Balance', finFmt(amt('cash_bank'))],
                ['Loans & Advances', finFmt(loansAdv)],
                ['Other Current Assets', finFmt(amt('other_current_assets'))],
                ['Total Assets', finFmt(totalAssets)],
              ];
              const plRows = [
                ['Revenue from Operations', finFmt(revOps)],
                ['Other Income', finFmt(otherInc)],
                ['Total Income', finFmt(totalInc)],
                ['Total Expenses', finFmt(totalExp)],
                ['Profit / (Loss) After Tax', finFmt(netPL)],
              ];
              const renderTbl = (rows) => (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                      <th className="text-left px-3 py-1.5 font-semibold">Particulars</th>
                      <th className="text-right px-3 py-1.5 font-semibold">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(([k, v]) => (
                      <tr key={k} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-semibold text-slate-700">{k}</td>
                        <td className="px-3 py-1.5 text-slate-700 text-right">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
              return (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-700 mb-1.5">Condensed Balance Sheet</p>
                      <div className="bg-white border border-slate-200 rounded overflow-hidden">{renderTbl(bsRows)}</div>
                      <p className="text-[9.5px] italic text-slate-500 mt-2 mb-1">Assets</p>
                      <div className="bg-white border border-slate-200 rounded overflow-hidden">{renderTbl(assetRows)}</div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-700 mb-1.5">Condensed Statement of Profit &amp; Loss</p>
                      <div className="bg-white border border-slate-200 rounded overflow-hidden">{renderTbl(plRows)}</div>
                    </div>
                  </div>
                  <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-2.5 mt-3">
                    <p className="text-[11px] font-bold text-teal-800 mb-1">Financial Observation</p>
                    <p className="text-[11px] text-slate-700 leading-relaxed">
                      {isVariantB
                        ? "Based on the latest available financial statements, the company's financial position is documented for reference. Final regularisation scope will depend on confirmation of pending filings, audit status, and the financial year cut-offs to be regularised under the CCFS 2026 window."
                        : 'Based on the latest available financial statements, the company appears operationally limited with a relatively manageable financial position from a closure execution perspective. Final closure readiness will depend on confirmation of liabilities, settlement status, and closure documentation requirements.'}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

          {/* PAGE 5 — Execution Roadmap (compact stepper, no arrows) */}
          <div className="pt-3">
            <p className="text-sm font-bold text-slate-800 border-b-2 border-teal-600 inline-block pb-0.5 mb-3">Execution Roadmap</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`roadmap-${lead.id}`}>
              {(isVariantB ? [
                ['1', 'Assessment', 'Initial review and regularisation scoping', 'Completed'],
                ['2', 'Validation Call', 'Confirm pending FYs and regularisation scope', 'To be scheduled'],
                ['3', 'Pending Filings Preparation', 'AOC-4 / MGT-7 preparation under CCFS 2026', 'R-PACE team'],
                ['4', 'Director & Board Approvals', 'Resolutions, signatures, and KYC support', 'Company'],
                ['5', 'ROC Filing & Confirmation', 'Filing under CCFS 2026 and acknowledgement', 'R-PACE team'],
              ] : [
                ['1', 'Assessment', 'Initial review and recommendation', 'Completed'],
                ['2', 'Validation Call', 'Confirm closure conditions and dependencies', 'To be scheduled'],
                ['3', 'Pending Filings', 'Compliance clean-up and filing preparation', 'R-PACE team'],
                ['4', 'Director & Shareholder Approvals', 'Resolutions, indemnities, and signatures', 'Company'],
                ['5', 'Closure Documentation', 'Statement of Accounts and document coordination', 'R-PACE team'],
                ['6', 'Regulatory Filing', 'STK-2 application and ROC submission', 'R-PACE team'],
                ['7', 'Company Closure Processing', 'Follow-up support until strike-off order', 'R-PACE team'],
              ]).map(([num, name, activity, resp], i, arr) => {
                const respLower = resp.toLowerCase();
                const chipColor = respLower === 'completed'
                  ? 'bg-emerald-100 text-emerald-700'
                  : respLower.includes('schedul')
                  ? 'bg-indigo-100 text-indigo-700'
                  : respLower.includes('r-pace')
                  ? 'bg-teal-50 text-teal-700'
                  : respLower === 'company'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-100 text-slate-600';
                return (
                  <div key={num} className={`flex items-center gap-3 px-3 py-1.5 ${i < arr.length - 1 ? 'border-b border-slate-100' : ''}`}>
                    <div className="bg-teal-50 text-teal-800 font-bold text-[12px] w-7 h-7 rounded flex items-center justify-center shrink-0">{num}</div>
                    <div className="font-semibold text-slate-800 text-[11.5px] w-[28%] shrink-0">{name}</div>
                    <div className="text-slate-500 text-[10.5px] flex-1">{activity}</div>
                    <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded shrink-0 ${chipColor}`}>{resp}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-700 leading-relaxed mt-4 mb-2">
              {isVariantB
                ? 'The following documents should be kept ready to support preparation and filing of the pending annual returns under the CCFS 2026 window.'
                : 'The following documents are generally required as part of the company closure filing process and are coordinated during execution support.'}
            </p>
            <div className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] font-bold text-teal-800 mb-2">
                {isVariantB ? 'Documents to Keep Ready for Regularisation' : 'Documents Required for Company Closure Filing'}
              </p>
              <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                      <th className="text-left px-3 py-2 font-semibold">Document</th>
                      <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                      <th className="text-left px-3 py-2 font-semibold">Who signs / provides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isVariantB ? [
                      ['Audited / Final Financials for pending FYs', 'Source data for AOC-4', 'Company'],
                      ['Board & Shareholder Resolutions', 'Approval of accounts and AGM', 'Company'],
                      ['Director DSC & DIN KYC', 'Required to sign and file forms', 'Each director'],
                      ["Auditor's Report and ADT-1 (if pending)", 'Audit closure and auditor appointment record', 'Company / auditor'],
                      ['Bank statements / ledgers (as needed)', 'Supporting records for financial preparation', 'Company'],
                    ] : [
                      ['Closure application (STK-2)', 'Main closure filing', '1 Director'],
                      ['Statement of Accounts (STK-8)', 'Confirms current financial position', 'Company + CA certification'],
                      ['Indemnity Bond (STK-3)', 'Director indemnity for closure filing', 'Each director'],
                      ['Affidavit (STK-4)', 'Director declaration supporting closure filing', 'Each director'],
                      ['Special Resolution / Consent Letters', 'Shareholder approval support', 'Shareholders / company'],
                      ['Latest Audited Financial Statements', 'Supporting financial record', 'Company'],
                      ['Regulator NOC, if applicable', 'Required only where sector regulator applies', 'Company / regulator'],
                    ]).map(r => (
                      <tr key={r[0]} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold text-slate-700">{r[0]}</td>
                        <td className="px-3 py-2 text-slate-700">{r[1]}</td>
                        <td className="px-3 py-2 text-slate-700">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* PAGE 6 — Next Steps and Engagement Support */}
          <div className="pt-3">
            <p className="text-sm font-bold text-slate-800 border-b-2 border-teal-600 inline-block pb-0.5 mb-3">Next Steps and Engagement Support</p>
            <p className="text-[10px] font-bold text-slate-700 mb-2">Estimated Director Involvement</p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <tbody>
                  {[
                    ['Validation and planning call', '60–90 minutes'],
                    ['Document sharing', '2–4 hours'],
                    ['Signatures and confirmations', 'Minimal, as required'],
                    ['Final review and approvals', 'Approximately 30 minutes'],
                  ].map(r => (
                    <tr key={r[0]} className="border-t border-slate-100 first:border-t-0">
                      <td className="px-3 py-2 font-semibold text-slate-700 w-3/5">{r[0]}</td>
                      <td className="px-3 py-2 text-slate-700">{r[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-teal-50 border-l-2 border-teal-600 rounded-r px-3 py-2.5 mt-3 mb-3">
              <p className="text-[11px] text-slate-700 leading-relaxed">
                The process is designed as an assisted execution model. Ratnatrraya coordinates documentation, filing preparation, approval sequencing, and regulatory follow-up so that director involvement remains limited and structured.
              </p>
            </div>
            <div className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] font-bold text-teal-800 mb-2">What Ratnatrraya Will Handle</p>
              <ul className="space-y-1.5">
                {(isVariantB ? [
                  'Review and regularisation planning for pending annual filings',
                  'Preparation of AOC-4 and MGT-7 filings under CCFS 2026 window',
                  'Drafting support for board resolutions and AGM minutes',
                  'Audit / accounts coordination support where required',
                  'Director KYC / DSC support and DIN reactivation where applicable',
                  'Filing and acknowledgement tracking on the MCA portal',
                  'Follow-up support for ROC queries during processing',
                ] : [
                  'Review and regularisation planning for pending annual filings',
                  'Coordination of closure documentation and filing sequence',
                  'Drafting support for resolutions, declarations, affidavits, and related papers',
                  'Coordination of shareholder approval process',
                  'Preparation and filing support for the closure application',
                  'Follow-up support for ROC queries during processing',
                  'Support for related statutory closure actions where applicable, including GST, EPFO, ESIC, PAN, and local registrations',
                ]).map(item => (
                  <li key={item} className="flex items-start gap-2 text-[11px] text-slate-700">
                    <span className="text-teal-600 font-bold shrink-0">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg p-3 mt-3">
              <p className="text-[10px] font-bold text-teal-800 mb-2">Items to Be Confirmed During Validation</p>
              <ul className="space-y-1.5">
                {(isVariantB ? [
                  'Director KYC documents',
                  'DSC availability details for directors',
                  'Latest available financial records / books of accounts',
                  'Bank statements for pending FYs',
                  'GST status confirmation',
                  'Status of statutory auditor and ADT-1 filings',
                  'Pending board resolutions or AGM minutes (if any)',
                  'Any notices, disputes, or litigation details',
                  'Confirmation of FY-wise filings to be regularised',
                ] : [
                  'Director KYC documents',
                  'DSC availability details for directors',
                  'Latest available financial records',
                  'Bank status confirmation',
                  'GST status confirmation',
                  'Any notices, disputes, or litigation details',
                  'Approximate date when business operations stopped',
                  'Whether any liabilities remain outstanding',
                  'Whether bank, GST, and other registrations require closure support',
                ]).map(item => (
                  <li key={item} className="flex items-start gap-2 text-[11px] text-slate-700">
                    <span className="text-teal-600 font-bold shrink-0">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg p-3 mt-3">
              <p className="text-[10px] font-bold text-teal-800 mb-2">Recommended Next Step</p>
              <p className="text-[11px] text-slate-700 leading-relaxed">
                {isVariantB
                  ? 'Schedule a validation and regularisation planning discussion to confirm pending FYs, identify any supporting records required, finalise the filing sequence, and initiate preparation for the AOC-4 and MGT-7 submissions under the CCFS 2026 window.'
                  : 'Schedule a validation and execution planning discussion to confirm closure readiness, identify any remaining dependencies, finalise the filing sequence, and initiate preparation for the company closure process.'}
              </p>
            </div>
            <div className="bg-white border-t-2 border-teal-600 border border-slate-200 rounded-lg p-3 mt-3">
              <p className="text-[10px] font-bold text-teal-800 mb-2">Important Disclaimer</p>
              <p className="text-[10px] text-slate-500 italic leading-relaxed">
                This report is based on a preliminary review of available MCA records and information available at the time of assessment. Final execution pathway and closure readiness remain subject to document validation, director confirmations, financial review, and regulatory verification. This report is intended as a readiness and execution assessment and should not be treated as a legal opinion or a guarantee of regulatory approval.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-[12px] font-bold text-slate-800">Ratnatrraya Consulting LLP</p>
              <p className="text-[11px] text-slate-600">R-PACE Compliance Desk</p>
              <p className="text-[11px] text-teal-700 mt-1">support@ratnatrraya.consulting</p>
            </div>
            <p className="text-center text-[9px] text-slate-400 mt-4 pt-3 border-t border-slate-100">
              {(snap?.company_name || lead.company_name || '—').toUpperCase()} &nbsp; | &nbsp; Confidential Assessment Report &nbsp; | &nbsp; Prepared on {report.generated_at?.slice(0, 10) || '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROPOSAL EDITOR (Journey Stage 11) ───

// ─── AUTO-DERIVED SCOPE ───
// Builds the recommended scope lines from current lead state. Each line carries `source`
// so the admin (and partner later) can see WHY a line is in scope.
function buildAutoScope(lead) {
  const ca = [];
  const cs = [];
  const matrix = lead.verified_filing_matrix || lead.draft_filing_matrix || lead.filing_matrix || {};
  const pendingFs = Object.keys(matrix).filter(fy => !matrix[fy]?.fs).sort();
  const pendingAr = Object.keys(matrix).filter(fy => !matrix[fy]?.ar).sort();
  const cinUp = (lead.mca_company_info?.cin || lead.cin || '').toString().toUpperCase();
  const isSection8 = cinUp.includes('NPL');
  const intent = (lead.intent_override || lead.quiz_answers?.[2] || '').toString().toLowerCase();
  const isCloseIntent = ['close', 'closed', 'strike_off'].includes(intent);
  const inactiveDirs = (lead.mca_directors || []).filter(d => {
    const s = (d.din_status || '').toLowerCase();
    return s && s !== 'active' && s !== 'verified';
  });

  // CA — Books / Audit / Filings prep
  if (pendingFs.length > 0) {
    ca.push({
      label: `Books preparation, statutory audit & AOC-4 filings — ${pendingFs.length} FY (${pendingFs.join(', ')})`,
      source: 'auto:filings',
    });
  }

  // CA — Statement of Accounts (only if closure)
  if (isCloseIntent && !isSection8) {
    ca.push({
      label: 'Statement of Accounts (STK-8) preparation + CA certification',
      source: 'auto:closure',
    });
  }

  // CS — Annual Return filings under CCFS window
  if (pendingAr.length > 0) {
    cs.push({
      label: `MGT-7 / MGT-7A filings under CCFS 2026 — ${pendingAr.length} FY (${pendingAr.join(', ')})`,
      source: 'auto:filings',
    });
  }

  // CS — DIN reactivation
  if (inactiveDirs.length > 0) {
    cs.push({
      label: `DIN reactivation for ${inactiveDirs.length} director${inactiveDirs.length === 1 ? '' : 's'} (DIR-3 KYC / DIR-5)`,
      source: 'auto:din',
    });
  }

  // CS — Closure path
  if (isCloseIntent && !isSection8) {
    cs.push({
      label: 'Closure application (STK-2) + STK-3 Indemnity Bond + STK-4 Affidavit',
      source: 'auto:closure',
    });
  }
  if (isCloseIntent && isSection8) {
    cs.push({
      label: 'Surrender of licence application under Section 8(6) + RoC liaison',
      source: 'auto:section8',
    });
  }

  // Discovery-call-driven scope additions
  const scopeChecks = lead.structured_call_notes?.scope_checkpoints || {};
  const _DISCOVERY = (typeof DISCOVERY_CHECKPOINTS !== 'undefined') ? DISCOVERY_CHECKPOINTS : [];
  for (const cp of _DISCOVERY) {
    if (!scopeChecks[cp.key]) continue;
    const entry = { label: cp.scope, source: `discovery:${cp.key}` };
    if (cp.side === 'CA') ca.push(entry);
    else if (cp.side === 'CS') cs.push(entry);
    // ADMIN-side checkpoints are noted but not added to CA/CS scope (admin handles offline)
  }

  // Admin-added overrides (free text the admin appends manually)
  const adminCA = lead.proposal?.admin_scope_ca || [];
  const adminCS = lead.proposal?.admin_scope_cs || [];
  for (const line of adminCA) ca.push({ label: line, source: 'admin' });
  for (const line of adminCS) cs.push({ label: line, source: 'admin' });

  // Admin-marked offline-handled (legal etc.)
  const adminOffline = [];
  for (const cp of _DISCOVERY) {
    if (scopeChecks[cp.key] && cp.side === 'ADMIN') adminOffline.push(cp.scope);
  }

  return { ca, cs, admin_offline: adminOffline };
}

function AutoDerivedScopePanel({ lead, onRefresh }) {
  const auto = buildAutoScope(lead);
  const [newCA, setNewCA] = useState('');
  const [newCS, setNewCS] = useState('');
  const [saving, setSaving] = useState(false);
  const adminCA = lead.proposal?.admin_scope_ca || [];
  const adminCS = lead.proposal?.admin_scope_cs || [];

  const persist = async (nextCA, nextCS) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const existing = lead.proposal || {};
      const payload = {
        ...existing,
        admin_scope_ca: nextCA,
        admin_scope_cs: nextCS,
      };
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/proposal`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        onRefresh?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save scope');
      }
    } catch { toast.error('Failed to save scope'); }
    finally { setSaving(false); }
  };

  const removeAdmin = (side, idx) => {
    if (side === 'CA') persist(adminCA.filter((_, i) => i !== idx), adminCS);
    else persist(adminCA, adminCS.filter((_, i) => i !== idx));
  };
  const addCA = () => { if (newCA.trim()) { persist([...adminCA, newCA.trim()], adminCS); setNewCA(''); } };
  const addCS = () => { if (newCS.trim()) { persist(adminCA, [...adminCS, newCS.trim()]); setNewCS(''); } };

  const sourceChip = (src) => {
    if (src.startsWith('auto:')) return <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">AUTO</span>;
    if (src.startsWith('discovery:')) return <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0">DISCOVERY</span>;
    if (src === 'admin') return <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">ADMIN</span>;
    return null;
  };

  const renderList = (lines, sideLabel, sideColor) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`scope-${sideLabel.toLowerCase()}-${lead.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] font-bold uppercase tracking-wide ${sideColor}`}>{sideLabel} Scope ({lines.length})</p>
      </div>
      {lines.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">No scope lines yet — drive by completing Filing Index, Eligibility, and the Discovery Call.</p>
      ) : (
        <ul className="space-y-1">
          {lines.map((line, i) => {
            const isAdmin = line.source === 'admin';
            const adminIdx = isAdmin
              ? lines.slice(0, i + 1).filter(x => x.source === 'admin').length - 1
              : -1;
            return (
              <li key={`${sideLabel}-${i}-${line.source}`} className="flex items-start gap-2 py-1 text-[11px]">
                {sourceChip(line.source)}
                <span className="text-slate-700 flex-1">{line.label}</span>
                {isAdmin && (
                  <button
                    onClick={() => removeAdmin(sideLabel, adminIdx)}
                    className="text-[9px] text-red-500 hover:text-red-700"
                    title="Remove admin-added line"
                    data-testid={`remove-${sideLabel.toLowerCase()}-${adminIdx}-${lead.id}`}
                  >✕</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`auto-scope-${lead.id}`}>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-start gap-2">
        <Zap className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-600">
          <strong>Auto-derived scope</strong> — re-computed live from filing gaps, intent, eligibility, DIN status, and Discovery Call checkpoints.
          Use the <span className="text-amber-700 font-semibold">+ Add admin line</span> input below to append anything specific.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {renderList(auto.ca, 'CA', 'text-blue-700')}
        {renderList(auto.cs, 'CS', 'text-emerald-700')}
      </div>

      {auto.admin_offline.length > 0 && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid={`offline-handled-${lead.id}`}>
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wide mb-1">Handled Offline by Admin</p>
          <ul className="space-y-0.5">
            {auto.admin_offline.map((line, i) => (
              <li key={i} className="text-[11px] text-slate-700">• {line}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex gap-2">
          <Input type="text" placeholder="+ Add CA scope line" value={newCA} onChange={e => setNewCA(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCA()} className="h-7 text-xs" data-testid={`add-ca-input-${lead.id}`} />
          <Button size="sm" onClick={addCA} disabled={!newCA.trim() || saving} className="h-7 text-xs bg-blue-600 hover:bg-blue-700">Add</Button>
        </div>
        <div className="flex gap-2">
          <Input type="text" placeholder="+ Add CS scope line" value={newCS} onChange={e => setNewCS(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCS()} className="h-7 text-xs" data-testid={`add-cs-input-${lead.id}`} />
          <Button size="sm" onClick={addCS} disabled={!newCS.trim() || saving} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">Add</Button>
        </div>
      </div>
    </div>
  );
}

function ProposalEditor({ lead, onDone }) {
  const existing = lead.proposal || null;
  // F8: Eligibility guardrail — warn if scope likely includes strike-off but decision is Not Eligible
  const eligDecision = lead.eligibility_engine?.decision;
  const notEligibleForStrikeoff = eligDecision === 'not_eligible_strikeoff' || eligDecision === 'not_eligible' || eligDecision === 'complex_case';
  const [timeline, setTimeline] = useState(existing?.timeline_weeks || '');
  const [govtFees, setGovtFees] = useState(existing?.fee_breakdown?.government_filing_fees || 0);
  const [profFees, setProfFees] = useState(existing?.fee_breakdown?.professional_fees || 0);
  const [gst, setGst] = useState(existing?.fee_breakdown?.gst || 0);
  const [paymentTerms, setPaymentTerms] = useState(existing?.payment_terms || '50% advance, balance before ROC filing');
  const [advancePct, setAdvancePct] = useState(existing?.advance_percentage || 50);
  const [assumptions, setAssumptions] = useState(existing?.assumptions || '');
  const [exclusions, setExclusions] = useState(existing?.exclusions || '');
  const [validityDays, setValidityDays] = useState(existing?.validity_days || 15);
  const [saving, setSaving] = useState(false);

  const total = Number(govtFees) + Number(profFees) + Number(gst);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const payload = {
        timeline_weeks: timeline,
        fee_breakdown: {
          government_filing_fees: Number(govtFees),
          professional_fees: Number(profFees),
          gst: Number(gst),
          total,
        },
        payment_terms: paymentTerms,
        advance_percentage: Number(advancePct),
        assumptions,
        exclusions,
        validity_days: Number(validityDays),
      };
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/proposal`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Proposal saved');
        onDone?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save proposal');
      }
    } catch { toast.error('Failed to save proposal'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      {existing && (
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium text-[10px]">
            v{existing.version}
          </span>
          <span className="text-slate-400">Last updated: {existing.updated_at?.slice(0, 10)} by {existing.updated_by}</span>
          {(lead.proposal_versions?.length || 0) > 0 && (
            <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
              <History className="w-3 h-3" /> {lead.proposal_versions.length} previous version(s)
            </span>
          )}
        </div>
      )}

      {/* F8: Eligibility guardrail banner */}
      {notEligibleForStrikeoff && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid={`proposal-guardrail-${lead.id}`}>
          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-bold text-red-800">Eligibility decision: Not Eligible for Strike-off</p>
            <p className="text-[11px] text-red-700 mt-0.5">{lead.eligibility_engine?.decision_reason || 'Strike-off blocked.'} Proposal scope should NOT include strike-off filings (STK-2, CCFS fee). Consider regularisation / continue path instead.</p>
          </div>
        </div>
      )}

      {/* Fee Breakdown */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Fee Breakdown</p>
        <div className="grid grid-cols-4 gap-2">
          <div>
            <label className="text-[9px] text-slate-400 block mb-0.5">Govt Filing Fees</label>
            <input type="number" value={govtFees} onChange={e => setGovtFees(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-govt-fees-${lead.id}`} />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 block mb-0.5">Professional Fees</label>
            <input type="number" value={profFees} onChange={e => setProfFees(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-prof-fees-${lead.id}`} />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 block mb-0.5">GST (18%)</label>
            <input type="number" value={gst} onChange={e => setGst(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-gst-${lead.id}`} />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 block mb-0.5">Total</label>
            <div className="w-full text-xs px-2 py-1.5 rounded border border-emerald-200 bg-emerald-50 font-bold text-emerald-700" data-testid={`proposal-total-${lead.id}`}>
              {fmt(total)}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline & Payment */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Timeline (weeks)</p>
          <input type="text" placeholder="e.g., 8-12" value={timeline} onChange={e => setTimeline(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-timeline-${lead.id}`} />
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Advance %</p>
          <input type="number" min={0} max={100} value={advancePct} onChange={e => setAdvancePct(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-advance-${lead.id}`} />
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Validity (days)</p>
          <input type="number" value={validityDays} onChange={e => setValidityDays(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-validity-${lead.id}`} />
        </div>
      </div>

      {/* Payment Terms */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Payment Terms</p>
        <input type="text" value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`proposal-payment-terms-${lead.id}`} />
      </div>

      {/* Assumptions & Exclusions */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Assumptions</p>
          <textarea placeholder="e.g., All directors cooperating, records available..." value={assumptions} onChange={e => setAssumptions(e.target.value)} rows={2} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white resize-y" data-testid={`proposal-assumptions-${lead.id}`} />
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Exclusions</p>
          <textarea placeholder="e.g., GST filings, Income Tax matters..." value={exclusions} onChange={e => setExclusions(e.target.value)} rows={2} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white resize-y" data-testid={`proposal-exclusions-${lead.id}`} />
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700"
          data-testid={`save-proposal-${lead.id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
          {existing ? `Save Proposal v${(existing.version || 0) + 1}` : 'Save Proposal v1'}
        </Button>
      </div>
    </div>
  );
}

// ─── PAYMENT TRACKER (Journey Stage 12) ───

const PAYMENT_TYPES = [
  { value: 'advance', label: 'Advance' },
  { value: 'milestone_1', label: 'Milestone 1' },
  { value: 'milestone_2', label: 'Milestone 2' },
  { value: 'final', label: 'Final Payment' },
];
const PAYMENT_MODES = ['UPI', 'Bank Transfer', 'Cheque', 'Cash', 'Other'];

function PaymentTracker({ lead, onDone }) {
  const [history, setHistory] = useState(lead.payment_history || []);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState('');
  const [type, setType] = useState('advance');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const totalPaid = history.reduce((s, p) => s + (p.amount || 0), 0);
  const proposalTotal = lead.proposal?.fee_breakdown?.total || 0;

  const recordPayment = async () => {
    if (!amount || Number(amount) <= 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/payments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(amount), date, mode, type, notes }),
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(prev => [...prev, data.payment]);
        setAmount(''); setNotes('');
        toast.success(data.message);
        onDone?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to record payment');
      }
    } catch { toast.error('Failed to record payment'); }
    finally { setSaving(false); }
  };

  const deletePayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/payments/${paymentId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setHistory(prev => prev.filter(p => p.id !== paymentId));
        toast.success('Payment deleted');
        onDone?.();
      }
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center gap-4 text-xs">
        <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${proposalTotal ? Math.min(100, (totalPaid / proposalTotal) * 100) : 0}%` }}
          />
        </div>
        <span className="font-bold text-emerald-700 whitespace-nowrap">{fmt(totalPaid)}</span>
        {proposalTotal > 0 && <span className="text-slate-400">/ {fmt(proposalTotal)}</span>}
      </div>

      {/* Payment History */}
      {history.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b text-slate-500">
                <th className="text-left px-3 py-1.5 font-semibold">Date</th>
                <th className="text-left px-3 py-1.5 font-semibold">Type</th>
                <th className="text-right px-3 py-1.5 font-semibold">Amount</th>
                <th className="text-left px-3 py-1.5 font-semibold">Mode</th>
                <th className="text-left px-3 py-1.5 font-semibold">Notes</th>
                <th className="px-2 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {history.map(p => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 text-slate-600">{p.date}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      p.type === 'advance' ? 'bg-blue-100 text-blue-700' :
                      p.type === 'final' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{p.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium text-emerald-700">{fmt(p.amount)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{p.mode || '—'}</td>
                  <td className="px-3 py-1.5 text-slate-400 truncate max-w-[150px]">{p.notes || '—'}</td>
                  <td className="px-2 py-1.5">
                    <button onClick={() => deletePayment(p.id)} className="text-slate-300 hover:text-red-500" data-testid={`del-payment-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Payment Form */}
      <div className="grid grid-cols-6 gap-2 items-end">
        <div>
          <label className="text-[9px] text-slate-400 block mb-0.5">Amount</label>
          <input type="number" placeholder="0" value={amount} onChange={e => setAmount(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`payment-amount-${lead.id}`} />
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block mb-0.5">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`payment-date-${lead.id}`} />
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block mb-0.5">Type</label>
          <select value={type} onChange={e => setType(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`payment-type-${lead.id}`}>
            {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block mb-0.5">Mode</label>
          <select value={mode} onChange={e => setMode(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`payment-mode-${lead.id}`}>
            <option value="">Select...</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block mb-0.5">Notes</label>
          <input type="text" placeholder="Optional" value={notes} onChange={e => setNotes(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" data-testid={`payment-notes-${lead.id}`} />
        </div>
        <Button size="sm" onClick={recordPayment} disabled={saving || !amount} className="h-[30px] text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`record-payment-${lead.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Record
        </Button>
      </div>
    </div>
  );
}

// ─── ENGAGEMENT LETTER (Journey Stage 13) ───

function EngagementLetterPanel({ lead, onDone }) {
  const [loading, setLoading] = useState(false);
  const [letterHtml, setLetterHtml] = useState(null);
  const [sent, setSent] = useState(lead.engagement_letter_sent || false);
  const hasProposal = !!lead.proposal;
  const hasAdvance = (lead.payment_history || []).some(p => p.type === 'advance') || lead.payment_status === 'paid';

  const generateLetter = async (sendEmail = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/engagement-letter`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_email: sendEmail }),
      });
      if (res.ok) {
        const data = await res.json();
        setLetterHtml(data.letter_html);
        if (data.sent) {
          setSent(true);
          toast.success(`Engagement letter sent to ${data.sent_to}`);
        } else {
          toast.success('Engagement letter preview generated');
        }
        onDone?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate letter');
      }
    } catch { toast.error('Failed to generate letter'); }
    finally { setLoading(false); }
  };

  if (!hasProposal) {
    return <p className="text-xs text-slate-400">Save a proposal first to generate an engagement letter.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          {sent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Sent</span>}
          {!hasAdvance && <span className="text-[10px] text-amber-600">Advance payment not yet recorded</span>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => generateLetter(false)} disabled={loading} className="h-7 text-xs gap-1 border-blue-300 text-blue-700" data-testid={`preview-letter-${lead.id}`}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} Preview
          </Button>
          <Button size="sm" onClick={() => generateLetter(true)} disabled={loading || !lead.email} className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" data-testid={`send-letter-${lead.id}`}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send Email
          </Button>
        </div>
      </div>

      {letterHtml && (
        <div className="border rounded-lg overflow-hidden bg-white" data-testid={`letter-preview-${lead.id}`}>
          <div className="max-h-[400px] overflow-y-auto p-2" dangerouslySetInnerHTML={{ __html: letterHtml }} />
        </div>
      )}
    </div>
  );
}

// ─── LEAD JOURNEY TIMELINE ───

function LeadJourneyTimeline({ lead }) {
  const [expanded, setExpanded] = useState(false);
  // Map old stage names to new for backward compatibility
  const STAGE_COMPAT = { new: 'enquiry', mca_downloaded: 'mca_master', filing_index_done: 'filing_index', eligibility_done: 'eligibility_check', quick_report_sent: 'quick_report', bs_done: 'bs_analysed', call_scheduled: 'intent_call', call_done: 'discovery_call', proposal_sent: 'proposal', lost: 'dropped' };
  const rawStage = lead.pipeline_stage || 'enquiry';
  const ps = STAGE_COMPAT[rawStage] || rawStage;

  const STAGE_ORDER = ['enquiry', 'mca_master', 'filing_index', 'eligibility_check', 'quick_report', 'intent_call', 'bs_analysed', 'discovery_call', 'detailed_report', 'proposal', 'won', 'dropped'];
  const currentIdx = STAGE_ORDER.indexOf(ps);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const fmtDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

  // Build journey stages from existing lead data
  const eligEngine = lead.eligibility_engine || {};
  const eligS1 = eligEngine.stage1?.checks || {};
  const eligPassed = Object.values(eligS1).filter(v => v === true).length;
  const bsItems = (lead.bs_analysis?.items) || {};
  const bsFilled = Object.values(bsItems).filter(v => v?.closure_path).length;
  const callNotes = lead.structured_call_notes || {};
  const callChecks = callNotes.checklist || {};
  const callDiscussed = Object.values(callChecks).filter(Boolean).length;
  const payments = lead.payment_history || [];
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const filingIndex = lead.filing_index || {};
  const filingGaps = (filingIndex.gaps || []);
  const shareholding = lead.shareholding || {};

  const stages = [
    {
      key: 'enquiry',
      label: 'Lead Created',
      timestamp: fmtDateTime(lead.created_at),
      summary: `Source: ${lead.source || 'website'}${lead.created_by_name ? ` | By: ${lead.created_by_name}` : ''}${lead.estimated_savings ? ` | Est. Savings: ${fmt(lead.estimated_savings)}` : ''}`,
    },
    {
      key: 'mca_master',
      label: 'MCA Master Uploaded',
      timestamp: fmtDate(lead.mca_uploaded_at),
      summary: lead.mca_company_info
        ? `CIN ${lead.mca_cin_match ? 'Verified' : 'Mismatch'} | ${lead.state_full_name || lead.cin_info?.state_code || ''} | ${lead.cin_info?.company_type_short || 'Pvt Ltd'} | Small: ${lead.small_company_analysis?.is_small_company ? 'Yes' : 'No'}`
        : null,
      pending: !lead.mca_company_info ? ['Upload MCA Excel from MCA portal'] : null,
    },
    {
      key: 'filing_index',
      label: 'Filing Index & Gap Mapping',
      timestamp: fmtDate(filingIndex.uploaded_at),
      summary: filingIndex.total_count
        ? `${filingIndex.total_count} filings | ${filingGaps.length} gaps | Last BS: ${lead.mca_company_info?.date_of_balance_sheet || 'N/A'}${filingGaps.filter(g => g.status === 'flagged').length > 0 ? ` | ${filingGaps.filter(g => g.status === 'flagged').length} pending` : ''}`
        : null,
      pending: !filingIndex.total_count ? ['Upload MCA Filing Index Excel'] : [
        ...(filingGaps.filter(g => g.status === 'flagged').length > 0 ? [`${filingGaps.filter(g => g.status === 'flagged').length} gaps still flagged`] : []),
      ],
    },
    {
      key: 'eligibility_check',
      label: 'Eligibility Engine Completed',
      timestamp: fmtDate(eligEngine.updated_at),
      summary: eligEngine.decision
        ? `${eligEngine.decision_label} | Stage 1: ${eligPassed}/5`
        : null,
      pending: !eligEngine.decision ? ['Complete 3-stage eligibility check'] : null,
    },
    {
      key: 'quick_report',
      label: 'Quick Report Generated',
      timestamp: fmtDate(lead.quick_report_generated_at),
      summary: lead.quick_report
        ? `${(lead.quick_report.observations || []).length} observations | Eligibility: ${eligEngine.decision_label || 'Pending'}`
        : null,
      pending: !lead.quick_report ? ['Generate Quick Report (post-eligibility)'] : null,
    },
    {
      key: 'intent_call',
      label: 'Intent Call',
      timestamp: fmtDate(lead.intent_call?.done_at),
      summary: lead.intent_call?.done_at
        ? `${({proceed:'Proceed',pause:'Paused',not_interested:'Not interested'})[lead.intent_call.outcome] || lead.intent_call.outcome}${lead.intent_call.notes ? ` · "${lead.intent_call.notes.slice(0, 60)}${lead.intent_call.notes.length > 60 ? '...' : ''}"` : ''}`
        : (STAGE_ORDER.indexOf(ps) > STAGE_ORDER.indexOf('intent_call') ? 'Initial intent discussion with client' : null),
      pending: !lead.intent_call?.done_at && STAGE_ORDER.indexOf(ps) >= STAGE_ORDER.indexOf('quick_report')
        ? ['Conduct brief intent call & mark done']
        : null,
    },
    {
      key: 'bs_analysed',
      label: 'Balance Sheet Analysis',
      timestamp: fmtDate(lead.bs_analysis?.updated_at),
      summary: lead.bs_analysis
        ? `${bsFilled}/11 items analyzed | SOA: ${lead.bs_analysis.soa_complexity || 'N/A'}${shareholding.total_shares ? ` | Shareholders: ${shareholding.shareholders?.length || 0}` : ''}`
        : null,
      pending: [
        ...(!lead.bs_analysis ? ['Upload AOC-4 and analyze BS'] : []),
        ...(bsFilled < 6 ? [`Complete BS Analysis (${bsFilled}/11 items)`] : []),
        ...(!shareholding.total_shares ? ['Add shareholding data'] : []),
      ].filter(Boolean),
    },
    {
      key: 'discovery_call',
      label: 'Discovery Call Completed',
      timestamp: fmtDate(callNotes.call_date),
      summary: callNotes.call_date
        ? `${callDiscussed}/9 items discussed${callNotes.caller_name ? ` | Caller: ${callNotes.caller_name}` : ''}${callNotes.summary ? ` | "${callNotes.summary.slice(0, 60)}${callNotes.summary.length > 60 ? '...' : ''}"` : ''}`
        : null,
      pending: !callNotes.call_date ? ['Conduct structured discovery call (9 checkpoints)'] : [
        ...(callDiscussed < 7 ? [`${9 - callDiscussed} call items not discussed`] : []),
      ],
    },
    {
      key: 'detailed_report',
      label: 'Detailed Report (Director Summary)',
      timestamp: fmtDate(lead.detailed_report?.generated_at),
      summary: [
        lead.detailed_report ? `Report v${lead.detailed_report.version}` : null,
        lead.cessation_fy_override ? `Cessation: ${lead.cessation_fy_override} (confirmed)` : null,
      ].filter(Boolean).join(' | ') || null,
      pending: [
        ...(!lead.detailed_report ? ['Generate Detailed Report'] : []),
      ],
    },
    {
      key: 'proposal',
      label: 'Proposal & Fee Quote',
      timestamp: fmtDate(lead.proposal?.updated_at),
      summary: lead.proposal
        ? `Proposal v${lead.proposal.version} — ${fmt(lead.proposal.fee_breakdown?.total)}`
        : null,
      pending: [
        ...(!lead.proposal ? ['Save Proposal with fee breakdown'] : []),
        ...(totalPaid === 0 ? ['Record advance payment'] : []),
        ...(!lead.engagement_letter_sent ? ['Send engagement letter'] : []),
      ],
    },
    {
      key: 'won',
      label: 'Case Created (Won)',
      timestamp: ps === 'won' ? fmtDate(lead.updated_at) : null,
      summary: ps === 'won' ? `Converted to R-PACE Case${totalPaid > 0 ? ` | ${fmt(totalPaid)} received` : ''}` : null,
      pending: ps !== 'won' && ps !== 'dropped' ? ['Convert lead to R-PACE Case after proposal + payment'] : null,
    },
  ];

  // Add lost stage if applicable
  if (ps === 'dropped') {
    stages.push({
      key: 'dropped',
      label: 'Lead Lost',
      timestamp: fmtDate(lead.lost_at),
      summary: `Reason: ${lead.lost_reason || 'Unknown'}${lead.lost_notes ? ` — ${lead.lost_notes}` : ''}`,
    });
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`lead-journey-${lead.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-1 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
        data-testid={`toggle-journey-${lead.id}`}
      >
        <p className="text-xs text-slate-600 font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-500" /> Lead Journey
          <span className="text-[10px] font-normal text-slate-400 ml-1">
            {currentIdx + 1}/{ps === 'dropped' ? stages.length : 11} stages
          </span>
        </p>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${expanded ? 'bg-indigo-100' : 'bg-slate-100 hover:bg-slate-200'} transition-colors`}>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-indigo-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 relative" data-testid={`journey-content-${lead.id}`}>
          {/* Vertical line */}
          <div className="absolute left-[9px] top-3 bottom-3 w-px bg-slate-200" />

          <div className="space-y-0.5">
            {stages.map((stage, i) => {
              const stageIdx = STAGE_ORDER.indexOf(stage.key);
              const isDone = stageIdx < currentIdx || (stageIdx === currentIdx && stage.key !== 'dropped');
              const isCurrent = stageIdx === currentIdx;
              const isFuture = stageIdx > currentIdx;
              const isLostStage = stage.key === 'dropped';

              // Show all stages (don't skip future ones)
              if (isLostStage && ps !== 'dropped') return null;

              return (
                <div key={stage.key} className="flex gap-3 pl-0 relative" data-testid={`journey-stage-${stage.key}`}>
                  {/* Dot */}
                  <div className={`w-[18px] h-[18px] rounded-full border-2 shrink-0 mt-1 z-10 flex items-center justify-center ${
                    isLostStage ? 'bg-red-500 border-red-500' :
                    isDone ? 'bg-emerald-500 border-emerald-500' :
                    isCurrent ? 'bg-amber-500 border-amber-500 ring-2 ring-amber-200' :
                    'bg-white border-slate-300'
                  }`}>
                    {isDone && !isCurrent && <Check className="w-2.5 h-2.5 text-white" />}
                    {isCurrent && !isLostStage && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    {isLostStage && <X className="w-2.5 h-2.5 text-white" />}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 pb-3 ${isFuture && !isLostStage ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold ${isLostStage ? 'text-red-700' : isDone ? 'text-slate-800' : isCurrent ? 'text-amber-800' : 'text-slate-400'}`}>
                        {stage.label}
                      </span>
                      {stage.timestamp && (
                        <span className="text-[10px] text-slate-400">{stage.timestamp}</span>
                      )}
                      {isCurrent && !isLostStage && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase">Current</span>
                      )}
                    </div>

                    {/* Summary for completed/current stages */}
                    {stage.summary && (isDone || isCurrent || isLostStage) && (
                      <p className={`text-[11px] mt-0.5 ${isLostStage ? 'text-red-600' : 'text-slate-500'}`}>{stage.summary}</p>
                    )}

                    {/* Pending items for current stage */}
                    {isCurrent && stage.pending && stage.pending.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {stage.pending.map((p, j) => (
                          <div key={j} className="flex items-center gap-1.5 text-[10px] text-amber-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                            {p}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pending items for future stages */}
                    {isFuture && !isLostStage && stage.pending && stage.pending.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {stage.pending.map((p, j) => (
                          <p key={j} className="text-[10px] text-slate-400">{p}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───

export default function AdminCCFSLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState({});
  const [stageFilter, setStageFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [stk1Filter, setStk1Filter] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
  }, [testMode]);

  const fetchLeads = async () => {
    try {
      const res = await ccfsLeadsAPI.getAll(testMode);
      // In test mode, show only test leads; otherwise show only real leads
      const filtered = testMode
        ? res.data.filter(l => l.is_test === true)
        : res.data.filter(l => l.is_test !== true);
      setLeads(filtered);
    } catch (error) {
      console.error('Error fetching CCFS leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLead = useCallback(async (leadId, field, value) => {
    setSaving(s => ({ ...s, [leadId]: true }));
    try {
      const res = await ccfsLeadsAPI.update(leadId, { [field]: value });
      setLeads(prev => prev.map(l => l.id === leadId ? res.data : l));
      toast.success('Saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(s => ({ ...s, [leadId]: false }));
    }
  }, []);

  const updateNestedField = useCallback(async (leadId, lead, section, field, value) => {
    setSaving(s => ({ ...s, [leadId]: true }));
    try {
      let payload;
      if (section === 'sop_checklist') {
        // Flat object — merge field directly
        const current = lead.sop_checklist || {};
        payload = { sop_checklist: { ...current, [field]: value } };
      } else if (section === 'backend_analysis') {
        // Nested object — field is subsection key (mca_data, filing_status, financial_snapshot), value is the full subsection
        const current = lead.backend_analysis || {};
        payload = { backend_analysis: { ...current, [field]: value } };
      } else {
        payload = { [section]: { ...(lead[section] || {}), [field]: value } };
      }
      const res = await ccfsLeadsAPI.update(leadId, payload);
      setLeads(prev => prev.map(l => l.id === leadId ? res.data : l));
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(s => ({ ...s, [leadId]: false }));
    }
  }, []);

  const deleteLead = useCallback(async (leadId) => {
    try {
      await ccfsLeadsAPI.delete(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      toast.success('Lead deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  }, []);

  const deleteAllLeads = useCallback(async () => {
    try {
      await ccfsLeadsAPI.deleteAll();
      setLeads([]);
      setShowDeleteConfirm(false);
      toast.success('All leads deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  }, []);

  const convertToCase = useCallback(async (leadId, opts = {}) => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/admin/cases/from-lead/${leadId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ca_partner_id: opts.ca_partner_id || null,
          cs_partner_id: opts.cs_partner_id || null,
          force_override: !!opts.force_override,
          override_reason: opts.override_reason || '',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Case created! Redirecting...');
        navigate(`/admin/cases/${data.id}`);
      } else {
        toast.error(data.detail || 'Failed to convert');
      }
    } catch {
      toast.error('Failed to convert');
    }
  }, [navigate]);

  const updatePipelineStage = useCallback(async (leadId, stage) => {
    setSaving(s => ({ ...s, [leadId]: true }));
    try {
      await ccfsLeadsAPI.updatePipelineStage(leadId, stage);
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, pipeline_stage: stage } : l));
      toast.success(`Stage updated to ${stage.replace(/_/g, ' ')}`);
    } catch (err) {
      toast.error('Failed to update stage');
    } finally {
      setSaving(s => ({ ...s, [leadId]: false }));
    }
  }, []);

  const markAsLost = useCallback(async (leadId, reason, notes) => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      await fetch(`${API}/api/ccfs-leads/${leadId}/mark-lost`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, notes }),
      });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, pipeline_stage: 'dropped', lost_reason: reason } : l));
      toast.success(`Lead marked as LOST: ${reason}`);
    } catch (err) {
      toast.error('Failed to mark as lost');
    }
  }, []);

  const uploadMCA = useCallback(async (leadId, file) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Only .xlsx files are accepted. Please download MCA Master Data in .xlsx format.');
      return;
    }
    setSaving(s => ({ ...s, [leadId]: true }));
    try {
      const res = await ccfsLeadsAPI.uploadMCA(leadId, file);
      const cin = res.data.cin_info;
      const sc = res.data.small_company_analysis;
      let msg = `MCA parsed: ${res.data.company_info_fields} fields, ${res.data.charges_count} charges, ${res.data.directors_count} directors`;
      if (cin?.valid) msg += ` | ${cin.company_type_short} (${cin.listing_status})`;
      if (sc) msg += sc.is_small_company ? ' | Small Company' : '';
      if (res.data.applicable_forms_count) msg += ` | ${res.data.applicable_forms_count} forms analyzed`;
      toast.success(msg);
      // Nudge admin to manually verify DIN status (MCA Excel does not carry live DIN status)
      if (res.data.directors_count > 0) {
        setTimeout(() => {
          toast.warning(
            `Please verify DIN Active Status for ${res.data.directors_count} director(s) in the MCA Master Data section.`,
            { duration: 6000 }
          );
        }, 400);
      }
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSaving(s => ({ ...s, [leadId]: false }));
    }
  }, []);

  const WEB_SOURCES = ['website', 'ccfs_calculator', 'website_claimed'];
  const COMPAT = { new: 'enquiry', mca_downloaded: 'mca_master', filing_index_done: 'filing_index', eligibility_done: 'eligibility_check', quick_report_sent: 'quick_report', bs_done: 'bs_analysed', call_scheduled: 'intent_call', call_done: 'discovery_call', proposal_sent: 'proposal', lost: 'dropped' };
  const normStage = (l) => COMPAT[l.pipeline_stage] || l.pipeline_stage || 'enquiry';
  const sourceFiltered = sourceFilter === 'ALL' ? leads : sourceFilter === 'website' ? leads.filter(l => WEB_SOURCES.includes(l.source || 'website')) : leads.filter(l => (l.source || 'website') === sourceFilter);
  const stk1Filtered = stk1Filter ? sourceFiltered.filter(l => l.filing_index?.gap_rules_meta?.notice_248_status === 'yes') : sourceFiltered;
  const stageFiltered = stageFilter === 'ALL' ? stk1Filtered : stk1Filtered.filter(l => normStage(l) === stageFilter);

  // Search filter
  const searchFiltered = !searchQuery.trim() ? stageFiltered : stageFiltered.filter(l => {
    const q = searchQuery.toLowerCase();
    return (l.company_name || '').toLowerCase().includes(q) ||
           (l.cin || '').toLowerCase().includes(q) ||
           (l.lead_number || '').toLowerCase().includes(q) ||
           (l.name || '').toLowerCase().includes(q);
  });

  // Sort
  const filteredLeads = [...searchFiltered].sort((a, b) => {
    if (sortBy === 'savings') return (b.estimated_savings || 0) - (a.estimated_savings || 0);
    if (sortBy === 'stage') {
      const order = { new: 0, mca_downloaded: 1, quick_report_sent: 2, call_scheduled: 3, call_done: 4, detailed_report: 5, won: 6, lost: 7 };
      return (order[a.pipeline_stage || 'enquiry'] || 0) - (order[b.pipeline_stage || 'enquiry'] || 0);
    }
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const stageCounts = sourceFiltered.reduce((acc, l) => { const s = normStage(l); acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const sourceCounts = leads.reduce((acc, l) => {
    const s = l.source || 'website';
    const key = WEB_SOURCES.includes(s) ? 'website' : s;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Summary stats
  const totalSavings = leads.reduce((sum, l) => sum + (l.estimated_savings || 0), 0);
  const wonCount = leads.filter(l => (l.pipeline_stage || 'enquiry') === 'won').length;
  const lostCount = leads.filter(l => (l.pipeline_stage || 'enquiry') === 'dropped').length;
  const activeCount = leads.length - wonCount - lostCount;
  const paidCount = leads.filter(l => l.payment_status === 'paid').length;
  const realizedSavings = leads.filter(l => (l.pipeline_stage || 'enquiry') === 'won').reduce((sum, l) => sum + (l.estimated_savings || 0), 0);
  const feePotential = leads.reduce((sum, l) => sum + ((l.proposal?.fee_breakdown?.total) || 0), 0);
  const billed = leads.reduce((sum, l) => sum + ((l.payment_history || []).reduce((s, p) => s + (p.amount || 0), 0)), 0);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-ccfs-leads">
      {/* Header Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CCFS Leads</h1>
          <p className="text-slate-500 text-sm">{testMode ? 'Test Mode — excluded from real stats' : 'Ready-to-Call Case System'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setTestMode(!testMode); setLoading(true); }}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${testMode ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
            data-testid="test-mode-toggle"
          >
            <span className={`w-2 h-2 rounded-full ${testMode ? 'bg-orange-500' : 'bg-slate-300'}`} />
            {testMode ? 'Test ON' : 'Test'}
          </button>
          {leads.length > 0 && (
            showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600 font-medium">Delete all?</span>
                <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={deleteAllLeads} data-testid="confirm-delete-all">Yes</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowDeleteConfirm(false)}>No</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-all-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Clear All
              </Button>
            )
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="leads-summary-stats">
        <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Leads</p>
          <div className="flex items-end gap-2">
            <span className="text-xl font-bold text-slate-800" data-testid="stat-total-leads">{leads.length}</span>
            <span className="text-xs text-slate-400 mb-0.5">Active <span className="font-semibold text-blue-600">{activeCount}</span> | Won <span className="font-semibold text-emerald-600">{wonCount}</span> | Lost <span className="font-semibold text-red-500">{lostCount}</span></span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-green-200 px-4 py-3">
          <p className="text-[10px] text-green-600 uppercase font-semibold mb-1">Savings</p>
          <div className="flex items-end gap-2">
            <span className="text-xl font-bold text-slate-800" data-testid="stat-total-savings">{fmt(totalSavings)}</span>
            <ArrowRight className="w-3 h-3 text-slate-300 mb-1" />
            <span className="text-xl font-bold text-emerald-600" data-testid="stat-realized-savings">{fmt(realizedSavings)}</span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] text-slate-400">Potential</span>
            <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${totalSavings > 0 ? Math.min(100, (realizedSavings / totalSavings) * 100) : 0}%` }} />
            </div>
            <span className="text-[10px] text-slate-400">Realized</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-amber-200 px-4 py-3">
          <p className="text-[10px] text-amber-600 uppercase font-semibold mb-1">Fee Potential</p>
          <div className="flex items-end gap-2">
            <span className="text-xl font-bold text-amber-700" data-testid="stat-fee-potential">{fmt(feePotential)}</span>
            <span className="text-xs text-slate-400 mb-0.5">Billed <span className="font-semibold text-emerald-600">{fmt(billed)}</span></span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-blue-200 px-4 py-3">
          <p className="text-[10px] text-blue-600 uppercase font-semibold mb-1">Paid</p>
          <p className="text-xl font-bold text-blue-700" data-testid="stat-paid">{paidCount}</p>
          <p className="text-[10px] text-slate-400">advance received</p>
        </div>
      </div>

      {/* Pipeline Flow Strip (clickable filter) */}
      <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 mb-4" data-testid="pipeline-bar">
        <div className="flex items-center gap-0.5 overflow-x-auto pb-1">
          <button
            onClick={() => setStageFilter('ALL')}
            className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full transition-all ${stageFilter === 'ALL' ? 'bg-[#1e3a5f] text-white' : 'text-slate-400 hover:bg-slate-100'}`}
            data-testid="pipeline-all"
          >All</button>
          {PIPELINE_STAGES.map((s, idx) => {
            const isActive = stageFilter === s.key;
            const stageIdx = PIPELINE_STAGES.findIndex(p => p.key === s.key);
            return (
              <div key={s.key} className="flex items-center gap-0.5 shrink-0">
                {idx > 0 && <ArrowRight className={`w-3 h-3 ${stageIdx <= PIPELINE_STAGES.findIndex(p => p.key === stageFilter) || stageFilter === 'ALL' ? 'text-slate-300' : 'text-slate-200'}`} />}
                <button
                  onClick={() => setStageFilter(isActive ? 'ALL' : s.key)}
                  className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full transition-all ${isActive ? `${PIPELINE_COLORS[s.key]} text-white` : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'}`}
                  data-testid={`pipeline-filter-${s.key}`}
                >
                  <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-white/70' : (stageCounts[s.key] || 0) > 0 ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                  {s.label}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search + Source Filter + Sort */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-0 max-w-sm">
          <Input
            placeholder="Search by company, CIN, lead #..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="leads-search"
          />
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Source filter */}
          <div className="flex items-center gap-1" data-testid="source-filter">
            {[['ALL', 'All'], ['website', 'Web'], ['r-pace', 'R-PACE'], ['admin', 'Admin']].map(([val, label]) => (
              <button key={val} onClick={() => setSourceFilter(val)}
                className={`text-xs font-medium px-2.5 py-1.5 rounded-full border transition-all ${sourceFilter === val ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                data-testid={`source-${val}`}
              >{label} {val !== 'ALL' ? `(${sourceCounts[val] || 0})` : ''}</button>
            ))}
          </div>

          {/* STK-1 case filter chip */}
          {(() => {
            const stk1Count = leads.filter(l => l.filing_index?.gap_rules_meta?.notice_248_status === 'yes').length;
            return (
              <button onClick={() => setStk1Filter(v => !v)}
                className={`text-xs font-bold px-2.5 py-1.5 rounded-full border transition-all flex items-center gap-1 ${stk1Filter ? 'bg-red-600 text-white border-red-700' : stk1Count > 0 ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100' : 'bg-white text-slate-400 border-slate-200'}`}
                disabled={stk1Count === 0}
                title={stk1Count === 0 ? 'No STK-1 cases in current view' : `Filter to ${stk1Count} STK-1 case(s) — time-sensitive`}
                data-testid="stk1-filter-chip"
              >
                <AlertCircle className="w-3 h-3" />
                STK-1{stk1Count > 0 ? ` (${stk1Count})` : ''}
              </button>
            );
          })()}

          <span className="hidden md:block w-px h-5 bg-slate-200" />

          {/* Sort */}
          <div className="flex items-center gap-1" data-testid="leads-sort">
            {[
              { key: 'newest', label: 'Newest' },
              { key: 'savings', label: 'Savings' },
              { key: 'stage', label: 'Stage' },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`text-[10px] font-medium px-2 py-1 rounded transition-all ${sortBy === s.key ? 'bg-slate-200 text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
                data-testid={`sort-${s.key}`}
              >{s.label}</button>
            ))}
          </div>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCcw className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{searchQuery ? `No leads matching "${searchQuery}"` : stageFilter === 'ALL' ? 'No CCFS leads yet' : `No leads in ${stageFilter} stage`}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              isExpanded={expandedId === lead.id}
              isSaving={saving[lead.id]}
              onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
              onUpdateTag={(field, value) => updateLead(lead.id, field, value)}
              onUpdateNested={(section, field, value) => updateNestedField(lead.id, lead, section, field, value)}
              onDelete={() => deleteLead(lead.id)}
              onConvertToCase={(opts) => convertToCase(lead.id, opts || {})}
              onUploadMCA={(file) => uploadMCA(lead.id, file)}
              onMarkLost={(reason, notes) => markAsLost(lead.id, reason, notes)}
              onRefresh={fetchLeads}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LEAD CARD ───

function IntentCallSection({ lead, onRefresh, onMarkLost, sectionKey, activeSection, setActiveSection }) {
  const stored = lead.intent_call || {};
  const done = !!stored.done_at;
  const [callDate, setCallDate] = useState(stored.call_date || new Date().toISOString().slice(0, 10));
  const [outcome, setOutcome] = useState(stored.outcome || '');
  const [notes, setNotes] = useState(stored.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCallDate(stored.call_date || new Date().toISOString().slice(0, 10));
    setOutcome(stored.outcome || '');
    setNotes(stored.notes || '');
  }, [stored.call_date, stored.outcome, stored.notes]);

  const submit = async ({ markDone }) => {
    if (markDone && !outcome) { toast.error('Select an outcome before marking done'); return; }
    if (markDone && !callDate) { toast.error('Call date is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/intent-call`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_date: callDate, outcome, notes, mark_done: markDone }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(markDone ? 'Intent Call marked done' : 'Intent Call saved');
      if (markDone && outcome === 'not_interested' && onMarkLost) {
        // Hand-off: trigger Mark Lost flow so admin captures reason & notes
        setTimeout(() => onMarkLost?.(lead), 400);
      }
      onRefresh?.();
    } catch { toast.error('Failed to save Intent Call'); }
    finally { setSaving(false); }
  };

  const outcomeLabels = {
    proceed: 'Proceed to detailed analysis',
    pause: 'Pause — client needs time',
    not_interested: 'Not interested — mark lost',
  };
  const outcomeColors = {
    proceed: 'bg-emerald-50 border-emerald-300 text-emerald-700',
    pause: 'bg-amber-50 border-amber-300 text-amber-700',
    not_interested: 'bg-red-50 border-red-300 text-red-700',
  };

  return (
    <CollapsibleSection
      icon={PhoneCall}
      title="Intent Call"
      borderColor="border-sky-200"
      iconColor="text-sky-600"
      bgHover="hover:bg-sky-50/30"
      testId={`intent-call-${lead.id}`}
      defaultOpen={!done && lead.pipeline_stage === 'quick_report'}
      sectionKey={sectionKey}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      summary={done
        ? `${outcomeLabels[stored.outcome] || stored.outcome} · ${new Date(stored.done_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
        : 'Quick intent check with client before BS Analysis'}
      badge={done ? (
        <span className={`ml-1 text-[9px] px-2 py-0.5 rounded-full font-bold border ${outcomeColors[stored.outcome] || 'bg-slate-100 text-slate-600'}`}>
          <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />Done
        </span>
      ) : (
        <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold">Pending</span>
      )}
    >
      <div className="space-y-3">
        <p className="text-[11px] text-slate-500 italic">Brief "are you actually moving forward?" check after sending the Quick Report. This gates the deeper BS Analysis and Discovery Call work.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Call Date</label>
            <input type="date" value={callDate} onChange={e => setCallDate(e.target.value)} disabled={done}
              className="w-full mt-1 text-xs px-2 py-1.5 rounded border border-slate-200 bg-white disabled:bg-slate-50 disabled:text-slate-500"
              data-testid={`intent-call-date-${lead.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Outcome</label>
            <div className="flex flex-col gap-1 mt-1">
              {Object.entries(outcomeLabels).map(([val, lbl]) => (
                <label key={val} className={`flex items-center gap-2 text-xs px-2 py-1 rounded border cursor-pointer ${outcome === val ? outcomeColors[val] : 'border-slate-200 bg-white hover:bg-slate-50'} ${done ? 'cursor-not-allowed opacity-70' : ''}`}>
                  <input type="radio" name={`intent-outcome-${lead.id}`} value={val} checked={outcome === val} onChange={() => setOutcome(val)} disabled={done}
                    className="w-3 h-3" data-testid={`intent-outcome-${val}-${lead.id}`} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Notes (optional)</label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} disabled={done}
            placeholder="e.g. Client wants to confirm with CA first; will revert in 3 days."
            className="w-full mt-1 text-xs px-2 py-1.5 rounded border border-slate-200 bg-white disabled:bg-slate-50 disabled:text-slate-500 resize-none"
            data-testid={`intent-notes-${lead.id}`} />
        </div>

        {done ? (
          <div className="flex items-center justify-between pt-1 border-t border-slate-100 pt-2">
            <span className="text-[10px] text-slate-500">
              Marked done by <strong>{stored.done_by || 'Admin'}</strong> on {new Date(stored.done_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => submit({ markDone: false })} disabled={saving} className="h-7 text-xs gap-1" data-testid={`intent-save-draft-${lead.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Draft
            </Button>
            <Button size="sm" onClick={() => submit({ markDone: true })} disabled={saving || !outcome || !callDate} className="h-7 text-xs gap-1 bg-sky-600 hover:bg-sky-700 text-white" data-testid={`intent-mark-done-${lead.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Mark Intent Call Done
            </Button>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function DirectorDINCell({ lead, director, idx, onRefresh, apiBase = '/ccfs-leads' }) {
  const savedStatus = director.din_status || 'not_verified';
  const isSaved = savedStatus !== 'not_verified';
  const [editing, setEditing] = useState(!isSaved);
  const [draft, setDraft] = useState(savedStatus);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(savedStatus); setEditing(!isSaved); }, [savedStatus, isSaved]);
  const statusColors = {
    not_verified: 'bg-slate-100 text-slate-500 border-slate-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    deactivated: 'bg-red-50 text-red-600 border-red-200',
    disqualified_164: 'bg-red-100 text-red-700 border-red-300',
    surrendered: 'bg-amber-50 text-amber-600 border-amber-200',
  };
  const statusLabels = {
    not_verified: 'Not Verified',
    active: 'Active',
    deactivated: 'Deactivated',
    disqualified_164: 'Disqualified u/s 164',
    surrendered: 'Surrendered',
  };
  const handleSave = async () => {
    if (!draft || draft === 'not_verified') { toast.error('Select a verified status'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api${apiBase}/${lead.id}/directors/${idx}/din-status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ din_status: draft }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(`DIN status saved — ${statusLabels[draft] || draft}`);
      setEditing(false);
      onRefresh?.();
    } catch { toast.error('Failed to update DIN status'); }
    finally { setSaving(false); }
  };
  const updatedAt = director.din_status_updated_at;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {editing ? (
        <>
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            className={`text-[10px] font-semibold px-2 py-1 rounded border cursor-pointer ${statusColors[draft] || statusColors.not_verified}`}
            data-testid={`din-status-select-${lead.id}-${idx}`}
          >
            <option value="not_verified">Not Verified</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
            <option value="disqualified_164">Disqualified u/s 164</option>
            <option value="surrendered">Surrendered</option>
          </select>
          <Button size="sm" className="h-6 text-[10px] px-2 gap-1 bg-[#1e3a5f] hover:bg-[#152d4a]" onClick={handleSave} disabled={saving || draft === 'not_verified'} data-testid={`din-status-save-${lead.id}-${idx}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Confirm & Save
          </Button>
          {isSaved && (
            <button type="button" onClick={() => { setDraft(savedStatus); setEditing(false); }} className="text-[10px] text-slate-500 underline hover:text-slate-700" data-testid={`din-status-cancel-${lead.id}-${idx}`}>Cancel</button>
          )}
        </>
      ) : (
        <>
          <span className={`text-[10px] font-semibold px-2 py-1 rounded border ${statusColors[savedStatus]}`} data-testid={`din-status-saved-${lead.id}-${idx}`}>
            <Lock className="w-2.5 h-2.5 inline mr-1" />{statusLabels[savedStatus] || savedStatus}
          </span>
          <button type="button" onClick={() => setEditing(true)} className="text-[10px] text-indigo-600 underline hover:text-indigo-800" data-testid={`din-status-edit-${lead.id}-${idx}`}>Edit</button>
          {updatedAt && (
            <span className="text-[9px] text-slate-400" title={`Updated on ${new Date(updatedAt).toLocaleString('en-IN')}${director.din_status_updated_by ? ' by ' + director.din_status_updated_by : ''}`}>
              {new Date(updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </>
      )}
    </div>
  );
}

function LeadCard({ lead, isExpanded, isSaving, onToggle, onUpdateTag, onUpdateNested, onDelete, onConvertToCase, onUploadMCA, onMarkLost, onRefresh }) {
  const navigate = useNavigate();
  // Accordion behaviour — only one major lead section is open at a time
  const [activeSection, setActiveSection] = useState(null);
  const [showMCA, setShowMCA] = useState(false);
  const [showFiling, setShowFiling] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [lostNotes, setLostNotes] = useState('');
  const fileInputRef = useRef(null);
  const [showPushPartner, setShowPushPartner] = useState(false);
  const [partnerSuggestions, setPartnerSuggestions] = useState([]);
  const [pushLoading, setPushLoading] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  // Convert dialog state
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertCA, setConvertCA] = useState('');
  const [convertCS, setConvertCS] = useState('');
  const [activePartners, setActivePartners] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  // Origin-lock override workflow
  const [overrideOriginLock, setOverrideOriginLock] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Derive origin-lock state from the lead itself
  const originPid = (lead.created_by_role === 'CA' || lead.created_by_role === 'CS') ? lead.created_by_id : null;
  const originRole = originPid ? lead.created_by_role : null;
  const originName = originPid ? (lead.created_by_name || 'Originating Partner') : '';

  // Verified filing matrix — editable by backend team
  const fyRange = buildFYRange(lead);
  // Plan B: if admin has never saved (no verified/draft/worksheet matrix), auto-suggest
  // ticking FS for all FYs ≤ last-filed-BS FY. AR stays unticked. Once saved (draft or
  // verified), the saved matrix wins and we never overwrite it.
  const hasSavedMatrix = !!(lead.verified_filing_matrix || lead.draft_filing_matrix || lead.filing_matrix);
  const existingMatrix = lead.verified_filing_matrix || lead.draft_filing_matrix || lead.filing_matrix || {};
  // Priority for initial matrix values:
  //   1. Saved verified/draft/legacy matrix (admin signed off here)
  //   2. Filing-Index FY Verification rows (admin signed off upstream — factors in extensions)
  //   3. BS-date heuristic (last-resort fallback for leads without Filing Index)
  const fromFyVerification = hasSavedMatrix ? null : deriveFilingMatrixFromFYVerification(lead, fyRange);
  const suggestedMatrix = (hasSavedMatrix || fromFyVerification) ? null : buildSuggestedFilingMatrix(lead, fyRange);
  const isSuggested = !!suggestedMatrix && getBsFyStartYear(lead) != null;
  const isFromFyVerification = !!fromFyVerification;
  const [filingMatrix, setFilingMatrix] = useState(() => {
    const m = {};
    for (const fy of fyRange) {
      if (hasSavedMatrix) {
        m[fy] = existingMatrix[fy] || { fs: false, ar: false };
      } else if (fromFyVerification) {
        m[fy] = fromFyVerification[fy] || { fs: false, ar: false };
      } else {
        m[fy] = suggestedMatrix[fy] || { fs: false, ar: false };
      }
    }
    return m;
  });
  const [filingDirty, setFilingDirty] = useState(isSuggested || isFromFyVerification);

  const statusCode = lead.quiz_answers?.[0] || '—';
  const filingInfo = lead.quiz_answers?.[1] || '—';
  const intentCode = lead.quiz_answers?.[2] || '—';
  const statusMeta = STATUS_MAP[statusCode];
  const intentMeta = INTENT_MAP[intentCode];
  const STAGE_COMPAT = { new: 'enquiry', mca_downloaded: 'mca_master', filing_index_done: 'filing_index', eligibility_done: 'eligibility_check', quick_report_sent: 'quick_report', bs_done: 'bs_analysed', call_scheduled: 'intent_call', call_done: 'discovery_call', proposal_sent: 'proposal', lost: 'dropped' };
  const pipelineStage = STAGE_COMPAT[lead.pipeline_stage] || lead.pipeline_stage || 'enquiry';
  const currentPipelineIdx = PIPELINE_STAGES.findIndex(s => s.key === pipelineStage);
  const hasMCA = !!lead.mca_company_info && Object.keys(lead.mca_company_info).length > 0;

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const adminToken = localStorage.getItem('ratnatraya_token');

  return (
    <Card className={`overflow-hidden ${lead.is_test ? 'border-orange-200 bg-orange-50/30' : ''} ${lead.payment_status === 'paid' ? 'border-l-[3px] border-l-green-500' : ''}`} data-testid={`lead-row-${lead.id}`}>
      {/* COLLAPSED ROW — Decluttered */}
      <div className="w-full text-left p-4 md:p-5 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-4">
          {/* Left — Lead identity */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0 relative">
              <Building2 className="w-5 h-5 text-slate-400" />
              {lead.lead_number && (
                <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-[#1e3a5f] text-white px-1 rounded" data-testid={`lead-number-badge-${lead.id}`}>
                  {lead.lead_number}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-900 truncate">{lead.company_name || 'No company name'}</p>
                {lead.documents_folder_url && (
                  <a href={lead.documents_folder_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-amber-500 hover:text-amber-700" title="Open Documents Folder" data-testid={`folder-btn-${lead.id}`}>
                    <FolderOpen className="w-4 h-4" />
                  </a>
                )}
                {lead.cin_duplicate_count > 1 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200 shrink-0" data-testid={`dup-badge-${lead.id}`}>
                    {lead.cin_duplicate_count} entries
                  </span>
                )}
                {(lead.created_by_role === 'CA' || lead.created_by_role === 'CS') && lead.created_by_name && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 shrink-0 flex items-center gap-1" title={`This lead was originated by ${lead.created_by_name} (${lead.created_by_role}). The ${lead.created_by_role} slot will be locked to this partner on conversion.`} data-testid={`partner-origin-${lead.id}`}>
                    <Star className="w-2.5 h-2.5 fill-current" />
                    Partner · {lead.created_by_role}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 truncate">
                {lead.cin && <span className="font-mono mr-1.5">{lead.cin}</span>}
                {lead.name || lead.email ? (
                  <span>{lead.name}{lead.email ? ` · ${lead.email}` : ''}</span>
                ) : (lead.created_by_name && (lead.created_by_role === 'CA' || lead.created_by_role === 'CS')) ? (
                  <span>{lead.created_by_name} ({lead.created_by_role})</span>
                ) : null}
              </p>
            </div>
          </div>

          {/* Middle — Key signals only */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {/* Pipeline Stage */}
            <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full text-white ${PIPELINE_COLORS[pipelineStage] || 'bg-slate-400'}`}>
              {(PIPELINE_STAGES.find(s => s.key === pipelineStage)?.label) || 'New'}
            </span>

            {/* Estimated Savings */}
            {lead.estimated_savings > 0 && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">{fmt(lead.estimated_savings)}</span>
            )}

            {/* Source badge */}
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
              (lead.source === 'r-pace') ? 'bg-blue-50 text-blue-600 border-blue-200' :
              (lead.source === 'admin') ? 'bg-slate-100 text-slate-500 border-slate-200' :
              'bg-emerald-50 text-emerald-600 border-emerald-200'
            }`}>
              {lead.source === 'r-pace' ? 'R-PACE' : lead.source === 'admin' ? 'Admin' : 'Web'}
            </span>

            {/* Payment + Test + Regime badges */}
            {lead.payment_status === 'paid' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`paid-badge-${lead.id}`}>PAID</span>}
            {lead.is_test && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-300">TEST</span>}
            {lead.filing_index?.gap_rules_meta?.notice_248_status === 'yes' && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 flex items-center gap-0.5" data-testid={`stk1-card-badge-${lead.id}`} title="ROC Notice u/s 248(1) / STK-1 received — case is time-sensitive">
                <AlertCircle className="w-2.5 h-2.5" /> STK-1 Case
              </span>
            )}
            {lead.regime_applied && lead.regime_applied !== 'ccfs_targeted' && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                lead.regime_applied === 'ccfs_filed' ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' :
                'bg-slate-100 text-slate-500 border border-slate-300'
              }`} data-testid={`regime-badge-${lead.id}`}>
                {lead.regime_applied === 'ccfs_filed' ? 'CCFS' : 'STD'}
              </span>
            )}
            {/* Ageing Flag */}
            {(() => {
              const daysSinceUpdate = Math.floor((Date.now() - new Date(lead.updated_at || lead.created_at).getTime()) / (1000 * 60 * 60 * 24));
              if (pipelineStage === 'won' || pipelineStage === 'dropped') return null;
              if (daysSinceUpdate > 14) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200" data-testid={`ageing-badge-${lead.id}`}>{daysSinceUpdate}d stale</span>;
              if (daysSinceUpdate > 7) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 border border-amber-200" data-testid={`ageing-badge-${lead.id}`}>{daysSinceUpdate}d</span>;
              return null;
            })()}
          </div>

          {/* Right — Date + Expand */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
            {isSaving && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>

        {/* Mobile: badges below main row */}
        <div className="flex md:hidden items-center gap-1.5 mt-2 flex-wrap">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full text-white ${PIPELINE_COLORS[pipelineStage] || 'bg-slate-400'}`}>
            {(PIPELINE_STAGES.find(s => s.key === pipelineStage)?.label) || 'New'}
          </span>
          {lead.estimated_savings > 0 && <span className="text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">{fmt(lead.estimated_savings)}</span>}
          {lead.payment_status === 'paid' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">PAID</span>}
          {lead.is_test && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600">TEST</span>}
        </div>
      </div>

      {/* EXPANDED DETAIL */}
      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          {/* Contact + Calculator Inputs */}
          <div className="p-4 md:p-5 space-y-4">

            {/* Workflow Tags + Quick Info Row */}
            <div className="flex items-center gap-2 flex-wrap">
              <TagDropdown value={lead.case_type} options={CASE_TYPES} colorMap={CASE_TYPE_COLORS} placeholder="Case Type" onChange={v => onUpdateTag('case_type', v)} testId={`tag-case-${lead.id}`} />
              <TagDropdown value={lead.complexity} options={COMPLEXITIES} colorMap={COMPLEXITY_COLORS} placeholder="Complexity" onChange={v => onUpdateTag('complexity', v)} testId={`tag-complexity-${lead.id}`} />
              <span className="hidden md:block w-px h-5 bg-slate-200" />
              {statusMeta && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusMeta.color}`}>{statusMeta.label}</span>}
              {intentMeta && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${intentMeta.color}`}>{intentMeta.label}</span>}
              {lead.is_not_sure && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-600 border-amber-200">Not Sure</span>}
              {lead.cta_type && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                  lead.cta_type === 'whatsapp' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  lead.cta_type === 'free_report' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  lead.cta_type === 'whatsapp_and_free_report' ? 'bg-violet-50 text-violet-700 border-violet-200' :
                  lead.cta_type === 'premium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-slate-50 text-slate-600 border-slate-200'
                }`}>
                  {lead.cta_type === 'whatsapp' ? 'WhatsApp' : lead.cta_type === 'free_report' ? 'Free Report' : lead.cta_type === 'whatsapp_and_free_report' ? 'WA + Report' : lead.cta_type === 'premium' ? 'Premium' : lead.cta_type}
                </span>
              )}
            </div>

            {/* Pipeline Flow (Read-only) */}
            <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
              <div className="flex items-center gap-0.5 overflow-x-auto">
                {PIPELINE_STAGES.map((s, idx) => {
                  // Stage-specific "definitely complete" marker — when present, render the pill
                  // as Past (green) even if it's still the pipeline's current_stage. This handles
                  // legacy leads whose stage never auto-advanced past intent_call / bs_analysed.
                  const stageCompleted = (
                    (s.key === 'mca_master' && !!lead.mca_company_info) ||
                    (s.key === 'filing_index' && (lead.filing_index?.total_count || 0) > 0) ||
                    (s.key === 'eligibility_check' && !!lead.eligibility_engine?.decision) ||
                    (s.key === 'quick_report' && !!lead.quick_report) ||
                    (s.key === 'intent_call' && !!lead.intent_call?.done_at) ||
                    (s.key === 'bs_analysed' && !!lead.bs_analysis?.soa_complexity) ||
                    (s.key === 'discovery_call' && !!lead.structured_call_notes?.call_date) ||
                    (s.key === 'detailed_report' && !!lead.detailed_report) ||
                    (s.key === 'proposal' && !!lead.proposal)
                  );
                  const isPast = idx < currentPipelineIdx || (stageCompleted && idx <= currentPipelineIdx);
                  const isActive = s.key === pipelineStage && !isPast;
                  const isDropped = s.key === 'dropped';
                  return (
                    <div key={s.key} className="flex items-center gap-0.5 shrink-0">
                      {idx > 0 && <ArrowRight className={`w-3 h-3 ${isPast ? 'text-emerald-300' : 'text-slate-200'}`} />}
                      <div title={s.label} className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full ${
                        isActive ? `${PIPELINE_COLORS[s.key]} text-white` :
                        isPast ? 'bg-emerald-50 text-emerald-600' :
                        isDropped && pipelineStage === 'dropped' ? 'bg-red-500 text-white' :
                        'text-slate-300'
                      }`} data-testid={`pipeline-${s.key}-${lead.id}`}>
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-white/70' : isPast ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                        <span className="whitespace-nowrap">{s.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Lead Journey Timeline */}
            <LeadJourneyTimeline lead={lead} />

            {/* Lead Origination Details */}
            <CollapsibleSection
              icon={User}
              title="Lead Origination Details"
              borderColor="border-slate-200"
              iconColor="text-slate-400"
              testId={`lead-origin-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!hasMCA}
              summary={`${lead.company_name || '—'} | ${lead.email || '—'} | ${lead.source || 'web'}${statusMeta ? ` | ${statusMeta.label}` : ''}${intentMeta ? ` | ${intentMeta.label}` : ''}`}
            >
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                {lead.cin && <div><span className="text-slate-400">CIN:</span> <span className="font-mono font-medium ml-1">{lead.cin}</span></div>}
                {lead.email && <div><span className="text-slate-400">Email:</span> <span className="ml-1">{lead.email}</span></div>}
                {lead.phone && <div><span className="text-slate-400">Phone:</span> <span className="ml-1">{lead.phone}</span></div>}
                <div><span className="text-slate-400">Submitted:</span> <span className="ml-1">{new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
                {lead.cessation_fy && <div><span className="text-slate-400">Stopped In:</span> <span className="ml-1 font-medium">{lead.cessation_fy}</span></div>}
                {lead.source && <div><span className="text-slate-400">Source:</span> <span className="ml-1">{lead.source}</span></div>}
              </div>
              {/* Status / Intent / Filing row */}
              <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-slate-100">
                {statusMeta && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusMeta.color}`}>{statusMeta.label}</span>}
                {intentMeta && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${intentMeta.color}`}>{intentMeta.label}</span>}
                {filingInfo && <span className="text-[10px] text-slate-500">Last Filing: {filingInfo}</span>}
                {lead.is_not_sure && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-600 border-amber-200">Not Sure</span>}
              </div>
              {lead.created_by_name && (lead.created_by_role === 'CA' || lead.created_by_role === 'CS') && (
                <div className="mt-2 pt-2 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-blue-400">Created By:</span> <span className="ml-1 font-medium">{lead.created_by_name} ({lead.created_by_role})</span></div>
                  {lead.created_by_email && <div><span className="text-blue-400">Partner Email:</span> <span className="ml-1">{lead.created_by_email}</span></div>}
                  {lead.created_by_mobile && <div><span className="text-blue-400">Partner Mobile:</span> <span className="ml-1">{lead.created_by_mobile}</span></div>}
                  {lead.created_by_firm && <div><span className="text-blue-400">Firm:</span> <span className="ml-1">{lead.created_by_firm}</span></div>}
                </div>
              )}
              {/* Related Leads with same CIN */}
              {lead.cin_related_leads?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200">
                  <p className="text-[10px] text-amber-700 font-semibold flex items-center gap-1.5 mb-1.5">
                    <AlertTriangle className="w-3 h-3" /> {lead.cin_duplicate_count} Leads With Same CIN
                  </p>
                  <div className="overflow-x-auto rounded border border-amber-200">
                    <table className="w-full text-[10px]">
                      <thead><tr className="bg-amber-50 border-b border-amber-200">
                        <th className="text-left px-2 py-1 text-amber-700 font-semibold">Lead #</th>
                        <th className="text-left px-2 py-1 text-amber-700 font-semibold">Company</th>
                        <th className="text-left px-2 py-1 text-amber-700 font-semibold">Source</th>
                        <th className="text-left px-2 py-1 text-amber-700 font-semibold">Stage</th>
                      </tr></thead>
                      <tbody>
                        {lead.cin_related_leads.map((rl, i) => (
                          <tr key={rl.lead_number || i} className="border-b border-amber-100 last:border-0">
                            <td className="px-2 py-1 font-mono text-[#1e3a5f]">{rl.lead_number || '—'}</td>
                            <td className="px-2 py-1">{rl.company_name || '—'}</td>
                            <td className="px-2 py-1">{rl.source === 'ccfs_calculator' ? 'Website' : rl.source || 'Website'}</td>
                            <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded-full text-white ${PIPELINE_COLORS[rl.pipeline_stage] || 'bg-slate-400'}`}>{(PIPELINE_STAGES.find(s => s.key === rl.pipeline_stage)?.label) || 'Enquiry'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CollapsibleSection>

            {/* Duplicate CIN — Related Leads */}

            {/* Financial Summary - compact */}
            {(lead.estimated_savings || lead.estimated_normal_fee || lead.estimated_ccfs_fee) && (
              <div className="flex items-center gap-3 flex-wrap">
                {lead.estimated_savings > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">Savings: {fmt(lead.estimated_savings)}</span>}
                {lead.estimated_ccfs_fee > 0 && <span className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1 rounded">CCFS Fee: {fmt(lead.estimated_ccfs_fee)}</span>}
                {lead.estimated_normal_fee > 0 && <span className="text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded">Normal Fee: {fmt(lead.estimated_normal_fee)}</span>}
              </div>
            )}

          </div>

          {/* ─── MCA MASTER DATA (same level as Filing Index) ─── */}
          <CollapsibleSection
              icon={Landmark}
              title="MCA Master Data"
              borderColor="border-slate-200"
              iconColor="text-slate-500"
              bgHover="hover:bg-slate-50/50"
              chevronBg="bg-slate-200"
              testId={`mca-section-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!!hasMCA && !lead.filing_index?.total_count}
              folderUrl={lead.documents_folder_url}
              badge={hasMCA ? <>
                {lead.mca_cin_match && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">CIN Verified</span>}
                {lead.mca_company_info?.company_status && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{lead.mca_company_info.company_status}</span>}
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">{lead.cin_info?.company_type_short || 'Unknown'}</span>
                {lead.small_company_analysis?.is_small_company && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Small Co</span>}
                {lead.state_full_name && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">{lead.state_full_name}</span>}
                {lead.mca_directors?.length > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200">{(lead.mca_directors || []).filter(d => d.din_status === 'active').length}/{lead.mca_directors.length} DIN Active</span>}
                {lead.mca_company_info?.date_of_balance_sheet && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Last BS: {lead.mca_company_info.date_of_balance_sheet}</span>}
              </> : null}
              headerRight={<div className="flex items-center gap-2">
                {lead.mca_file_name && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const res = await ccfsLeadsAPI.downloadMCA(lead.id);
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a'); a.href = url; a.download = lead.mca_file_name; a.click();
                      window.URL.revokeObjectURL(url);
                    } catch { toast.error('Download failed'); }
                  }} data-testid={`download-mca-${lead.id}`}>
                    <Download className="w-3 h-3" /> {lead.mca_file_name}
                  </Button>
                )}
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { if (e.target.files[0]) { onUploadMCA(e.target.files[0]); e.target.value = ''; } }} />
                {hasMCA ? (
                  <span className="h-7 text-[10px] px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-400 flex items-center gap-1" title={lead.mca_uploaded_at ? `MCA data is locked once parsed. Uploaded on ${new Date(lead.mca_uploaded_at).toLocaleString('en-IN')}` : 'MCA data is locked once parsed. Delete lead to re-upload.'} data-testid={`mca-locked-${lead.id}`}>
                    <Lock className="w-3 h-3" /> Locked
                    {lead.mca_uploaded_at && <span className="text-slate-400 font-normal ml-1 border-l border-slate-300 pl-1">{new Date(lead.mca_uploaded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                  </span>
                ) : (
                  <Button size="sm" className="h-7 text-xs bg-[#1e3a5f] hover:bg-[#152d4a] gap-1" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={isSaving} data-testid={`upload-mca-${lead.id}`}>
                    {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload Excel
                  </Button>
                )}
              </div>}
            >

              {hasMCA ? (
                <MCAMasterDataPanel
                  lead={lead}
                  onRefresh={onRefresh}
                  apiBase="/ccfs-leads"
                  DinStatusCell={DirectorDINCell}
                />
              ) : (
                <p className="text-xs text-slate-400">No MCA data uploaded yet. Upload the Excel file from MCA portal to auto-populate company data.</p>
              )}
            </CollapsibleSection>

          {/* ─── MCA FILING INDEX ─── */}
          <FilingIndexSection lead={lead} onRefresh={onRefresh} sectionKey={`filing-index-${lead.id}`} activeSection={activeSection} setActiveSection={setActiveSection} />

          {/* ─── VERIFIED FILING STATUS (Year-wise FS / AR — admin's signed-off matrix) ─── */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={(e) => { e.stopPropagation(); setShowFiling(!showFiling); }}
              className="w-full flex items-center justify-between p-4 md:px-5 hover:bg-slate-50/50 transition-colors"
              data-testid={`toggle-filing-${lead.id}`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <ClipboardCheck className="w-4 h-4 text-[#1e3a5f]" /> Year-wise Filing Status (FS / AR)
                {lead.verified_filing_matrix && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">Verified</span>}
                {!lead.verified_filing_matrix && lead.draft_filing_matrix && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Draft</span>}
                {!lead.verified_filing_matrix && !lead.draft_filing_matrix && lead.filing_matrix && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">From Worksheet</span>}
                {!hasSavedMatrix && isFromFyVerification && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium" data-testid={`vfm-from-fy-verification-${lead.id}`} title="Pre-filled from Filing Index FY Verification (which factors in extensions, late filings, and admin overrides). Adjust as needed and save.">From Filing Index Verification</span>}
                {!hasSavedMatrix && !isFromFyVerification && isSuggested && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium" data-testid={`vfm-suggested-badge-${lead.id}`}>Suggested</span>}
              </span>
              {showFiling ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {showFiling && (
              <div className="px-4 md:px-5 pb-5">
                {fyRange.length === 0 ? (
                  <div className="p-3 rounded-lg bg-slate-50 border border-dashed border-slate-300 text-center" data-testid={`vfm-empty-state-${lead.id}`}>
                    <p className="text-xs text-slate-500">
                      Upload MCA Master Data first to enable verification — incorporation date is required to set the FY range.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-3">
                      {lead.verified_filing_matrix ? 'Verified filing status based on MCA documents.' : lead.draft_filing_matrix ? 'Draft saved. Continue verifying and save as verified when complete.' : lead.filing_matrix ? 'Pre-populated from worksheet. Verify against actual MCA documents and save.' : isFromFyVerification ? <>Pre-filled from <strong>Filing Index → FY Verification</strong> (which factors in MCA-extension filings, late filings, and admin overrides). Review and save.</> : isSuggested ? <>Auto-suggested from last filed BS date (<strong>{lead.mca_company_info?.date_of_balance_sheet}</strong>). FS ticked for all FYs ≤ BS year. Adjust gap years &amp; AR as needed, then save.</> : 'Mark each year\'s FS (AOC-4) and AR (MGT-7) filing status based on MCA paid documents.'}
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b">
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Financial Year</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">FS (AOC-4)</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">AR (MGT-7)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Date-based "Not Yet Due" computation (mirrors upper MCA Filing Index panel).
                            // AOC-4 due 30 Oct of (FY+1); MGT-7 due 29 Nov of (FY+1). Day-precise.
                            const today = new Date();
                            const todayYear = today.getFullYear();
                            const todayMonth = today.getMonth() + 1;
                            const todayDay = today.getDate();
                            const fyStartYear = (fyStr) => parseInt(String(fyStr).split('-')[0], 10);
                            const isFsNotYetDue = (fyStr) => {
                              const sy = fyStartYear(fyStr);
                              if (isNaN(sy)) return false;
                              const dueYear = sy + 1;
                              if (todayYear < dueYear) return true;
                              if (todayYear > dueYear) return false;
                              return (todayMonth < 10) || (todayMonth === 10 && todayDay < 30);
                            };
                            const isArNotYetDue = (fyStr) => {
                              const sy = fyStartYear(fyStr);
                              if (isNaN(sy)) return false;
                              const dueYear = sy + 1;
                              if (todayYear < dueYear) return true;
                              if (todayYear > dueYear) return false;
                              return (todayMonth < 11) || (todayMonth === 11 && todayDay < 29);
                            };
                            return fyRange.map(fy => {
                              const entry = filingMatrix[fy] || { fs: false, ar: false };
                              const fsNotDue = !entry.fs && isFsNotYetDue(fy);
                              const arNotDue = !entry.ar && isArNotYetDue(fy);
                              return (
                                <tr key={fy} className="border-b last:border-0 hover:bg-slate-50/50" data-testid={`vfm-row-${fy}-${lead.id}`}>
                                  <td className="px-3 py-2 font-medium text-slate-700">FY {fy}</td>
                                  <td className="text-center px-3 py-2">
                                    {fsNotDue ? (
                                      <span className="text-[10px] text-slate-400 italic" data-testid={`vfm-fs-not-yet-due-${fy}-${lead.id}`}>Not Yet Due</span>
                                    ) : (
                                      <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                        <Checkbox
                                          checked={entry.fs}
                                          onCheckedChange={() => {
                                            const updated = { ...filingMatrix, [fy]: { ...entry, fs: !entry.fs } };
                                            setFilingMatrix(updated);
                                            setFilingDirty(true);
                                          }}
                                          data-testid={`vfm-fs-${fy}-${lead.id}`}
                                        />
                                        <span className={`text-xs ${entry.fs ? 'text-green-600' : 'text-red-500 font-semibold'}`}>
                                          {entry.fs ? 'Filed' : 'Not Filed'}
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="text-center px-3 py-2">
                                    {arNotDue ? (
                                      <span className="text-[10px] text-slate-400 italic" data-testid={`vfm-ar-not-yet-due-${fy}-${lead.id}`}>Not Yet Due</span>
                                    ) : (
                                      <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                        <Checkbox
                                          checked={entry.ar}
                                          onCheckedChange={() => {
                                            const updated = { ...filingMatrix, [fy]: { ...entry, ar: !entry.ar } };
                                            setFilingMatrix(updated);
                                            setFilingDirty(true);
                                          }}
                                          data-testid={`vfm-ar-${fy}-${lead.id}`}
                                        />
                                        <span className={`text-xs ${entry.ar ? 'text-green-600' : 'text-red-500 font-semibold'}`}>
                                          {entry.ar ? 'Filed' : 'Not Filed'}
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                    {filingDirty && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTag('draft_filing_matrix', filingMatrix);
                            setFilingDirty(false);
                            toast.success('Draft saved — you can continue later');
                          }}
                          disabled={isSaving}
                          data-testid={`save-draft-filing-${lead.id}`}
                        >
                          {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Draft
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#1e3a5f] hover:bg-[#152d4a] gap-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTag('verified_filing_matrix', filingMatrix);
                            setFilingDirty(false);
                            toast.success('Filing status verified and saved');
                          }}
                          disabled={isSaving}
                          data-testid={`save-filing-${lead.id}`}
                        >
                          {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save as Verified
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ─── ELIGIBILITY ENGINE (3-Stage) ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={ShieldCheck}
              title="R-PACE Eligibility Engine"
              borderColor="border-slate-200"
              iconColor="text-violet-500"
              bgHover="hover:bg-violet-50/30"
              chevronBg="bg-violet-100"
              testId={`eligibility-engine-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!lead.eligibility_engine?.decision}
              summary={(() => {
                const intent = (lead.quiz_answers?.[2] || lead.gap_rules_meta?.intent || lead.intent || '').toString().toLowerCase().trim();
                if (intent === 'continue') return 'CCFS Eligible — Regularise';
                return DISPLAY_DECISION_LABEL[lead.eligibility_engine?.decision] || lead.eligibility_engine?.decision_label || 'Not started';
              })()}
              badge={(() => {
                const intent = (lead.quiz_answers?.[2] || lead.gap_rules_meta?.intent || lead.intent || '').toString().toLowerCase().trim();
                const effDec = intent === 'continue' ? 'ccfs_regularisation' : lead.eligibility_engine?.decision;
                if (!effDec) return null;
                const effLabel = intent === 'continue'
                  ? 'CCFS Eligible — Regularise'
                  : (DISPLAY_DECISION_LABEL[effDec] || lead.eligibility_engine.decision_label || effDec);
                return (
                  <span className={`ml-2 text-[9px] px-2 py-0.5 rounded-full font-bold border ${DECISION_COLORS[effDec] || ''}`}>
                    {effLabel}
                  </span>
                );
              })()}
            >
              <EligibilityEngine lead={lead} onRefresh={onRefresh} />
            </CollapsibleSection>
          )}


          {/* ─── INTENT CALL (gate between Eligibility check and detailed analysis) ─── */}
          {hasMCA && lead.eligibility_engine?.decision !== 'not_eligible' && (
            <IntentCallSection lead={lead} onRefresh={onRefresh} onMarkLost={onMarkLost}
              sectionKey={`intent-call-${lead.id}`} activeSection={activeSection} setActiveSection={setActiveSection} />
          )}

          {/* ─── BS ANALYSIS (only if eligibility != not_eligible) ─── */}
          {hasMCA && lead.eligibility_engine?.decision !== 'not_eligible' && (
            <CollapsibleSection
              icon={ClipboardCheck}
              title="Balance Sheet Analysis & Closure Paths"
              borderColor="border-slate-200"
              iconColor="text-slate-500"
              bgHover="hover:bg-slate-50/50"
              chevronBg="bg-slate-200"
              testId={`bs-analysis-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!lead.bs_analysis?.soa_complexity}
              folderUrl={lead.documents_folder_url}
              summary={lead.bs_analysis ? `SOA: ${lead.bs_analysis.soa_complexity || 'N/A'} | ${Object.values(lead.bs_analysis.items || {}).filter(v => v?.closure_path).length}/24 items` : 'Not started'}
              badge={(() => {
                const allItems = [...Object.values(lead.bs_analysis?.items || {}), ...Object.values(lead.bs_analysis?.pl_items || {})];
                const redN = allItems.filter(v => v?.discovery_flag === 'red').length;
                const amberN = allItems.filter(v => v?.discovery_flag === 'amber').length;
                const intentPending = !lead.intent_call?.done_at;
                return (
                  <>
                    {intentPending && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold" title="Mark Intent Call done first for a cleaner pipeline">Intent Call pending</span>}
                    {lead.aoc4_parsed && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">AOC-4 Parsed</span>}
                    {lead.aoc4_parsed?.locked && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> Locked</span>}
                    {lead.bs_analysis?.locked && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold inline-flex items-center gap-0.5" data-testid={`bs-locked-badge-${lead.id}`}><Lock className="w-2.5 h-2.5" /> BS Locked</span>}
                    {redN > 0 && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold inline-flex items-center gap-0.5"><Flag className="w-2.5 h-2.5" fill="currentColor" /> {redN}</span>}
                    {amberN > 0 && <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold inline-flex items-center gap-0.5"><Flag className="w-2.5 h-2.5" fill="currentColor" /> {amberN}</span>}
                  </>
                );
              })()}
            >
              <BSAnalysisPanel lead={lead} onRefresh={onRefresh} onSave={async (bsData) => {
                try {
                  const token = localStorage.getItem('ratnatraya_token');
                  await fetch(`${API}/api/ccfs-leads/${lead.id}/bs-analysis`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(bsData),
                  });
                  toast.success('BS Analysis saved');
                  onRefresh?.();
                } catch { toast.error('Failed to save BS analysis'); }
              }} />
            </CollapsibleSection>
          )}

          {/* ─── SHAREHOLDING STATUS (NEW) ─── */}
          {hasMCA && lead.eligibility_engine?.decision !== 'not_eligible' && (
            <ShareholdingPanel lead={lead} onRefresh={onRefresh} />
          )}

          {/* ─── ENGAGEMENT OVERRIDE (Cessation FY + Client Intent) ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={CalendarClock}
              title="Engagement Override (Cessation & Intent)"
              borderColor="border-amber-200"
              iconColor="text-amber-500"
              bgHover="hover:bg-amber-50/30"
              chevronBg="bg-amber-100"
              testId={`cessation-override-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={false}
              summary={(() => {
                const parts = [];
                parts.push(lead.cessation_fy_override ? `Cessation: ${lead.cessation_fy_override}` : `Cessation: ${lead.cessation_fy || 'Not set'}`);
                if (lead.intent_override) parts.push(`Intent: ${lead.intent_override}`);
                return parts.join(' · ');
              })()}
              badge={(lead.cessation_fy_override || lead.intent_override) && (
                <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">OVERRIDDEN</span>
              )}
            >
              {/* BS date prompt */}
              {lead.mca_company_info?.date_of_balance_sheet && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  <p className="text-[10px] text-blue-700">
                    Last filed BS date from MCA: <strong>{fmtDatePretty(lead.mca_company_info.date_of_balance_sheet)}</strong>. 
                    CCFS lead-level cessation info ({lead.cessation_fy || 'not set'}) may be inaccurate — verify against actual BS data.
                  </p>
                </div>
              )}
              <CessationOverridePanel lead={lead} onDone={onRefresh} />
            </CollapsibleSection>
          )}

          {/* ─── STRUCTURED CALL NOTES ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={MessageSquare}
              title="Discovery Call Notes"
              borderColor="border-sky-200"
              iconColor="text-sky-500"
              bgHover="hover:bg-sky-50/30"
              chevronBg="bg-sky-100"
              testId={`call-notes-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!lead.structured_call_notes?.call_date}
              summary={lead.structured_call_notes?.call_date ? `${Object.values(lead.structured_call_notes?.checklist || {}).filter(Boolean).length}/9 items | ${lead.structured_call_notes.caller_name || ''}` : 'No call recorded'}
            >
              <StructuredCallNotes lead={lead} onSave={async (notesData) => {
                try {
                  const token = localStorage.getItem('ratnatraya_token');
                  await fetch(`${API}/api/ccfs-leads/${lead.id}/call-notes`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(notesData),
                  });
                  toast.success('Call notes saved');
                  onRefresh?.();
                } catch { toast.error('Failed to save call notes'); }
              }} />
            </CollapsibleSection>
          )}

          {/* ─── SAVINGS EVOLUTION ─── */}
          {(lead.savings_history || []).length > 1 && (
            <div className="bg-white rounded-lg border border-amber-200 p-4" data-testid={`savings-evolution-${lead.id}`}>
              <p className="text-[10px] text-amber-600 uppercase tracking-wide font-semibold flex items-center gap-1.5 mb-3">
                <TrendingUp className="w-3.5 h-3.5" /> Savings Evolution
              </p>
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {(lead.savings_history || []).map((entry, i) => {
                  const stageLabels = { lead_created: 'Lead Created', intent_call: 'Intent Call', detailed_report: 'Detailed Report', manual: 'Manual' };
                  const prev = i > 0 ? lead.savings_history[i - 1].amount : null;
                  const diff = prev !== null ? entry.amount - prev : 0;
                  return (
                    <div key={entry.timestamp || i} className="flex items-center gap-1.5 shrink-0">
                      {i > 0 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                      <div className={`px-2.5 py-1.5 rounded-lg border text-center ${i === (lead.savings_history || []).length - 1 ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
                        <p className="text-[9px] text-slate-400 uppercase">{stageLabels[entry.stage] || entry.stage}</p>
                        <p className="text-xs font-bold text-slate-800">{fmt(entry.amount)}</p>
                        {diff !== 0 && <p className={`text-[9px] font-medium ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>{diff > 0 ? '+' : ''}{fmt(diff)}</p>}
                        <p className="text-[8px] text-slate-400">{entry.timestamp?.slice(0, 10)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── DETAILED REPORT (Journey Stage 10) ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={FileBarChart}
              title="Detailed Compliance Report"
              borderColor="border-violet-200"
              iconColor="text-violet-500"
              bgHover="hover:bg-violet-50/30"
              chevronBg="bg-violet-100"
              testId={`detailed-report-section-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!lead.detailed_report}
              summary={lead.detailed_report ? `v${lead.detailed_report.version} — Director's Summary` : 'Not generated'}
              badge={lead.detailed_report && (
                <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">
                  v{lead.detailed_report.version}
                </span>
              )}
            >
              <DetailedReportPanel lead={lead} onDone={onRefresh} />
            </CollapsibleSection>
          )}

          {/* ─── PROPOSAL & FEE ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={Receipt}
              title="Proposal & Fee Breakdown"
              borderColor="border-emerald-200"
              iconColor="text-emerald-600"
              bgHover="hover:bg-emerald-50/30"
              chevronBg="bg-emerald-100"
              testId={`proposal-section-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={!lead.proposal}
              summary={lead.proposal ? `v${lead.proposal.version} — ${fmt(lead.proposal.fee_breakdown?.total)}` : 'Not created'}
              badge={lead.proposal && (
                <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  v{lead.proposal.version} — {fmt(lead.proposal.fee_breakdown?.total)}
                </span>
              )}
            >
              <AutoDerivedScopePanel lead={lead} onRefresh={onRefresh} />
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Fee Breakdown, Timeline & Terms</p>
                <ProposalEditor lead={lead} onDone={onRefresh} />
              </div>
            </CollapsibleSection>
          )}

          {/* ─── PAYMENT TRACKER ─── */}
          {hasMCA && (
            <CollapsibleSection
              icon={DollarSign}
              title="Payment Tracker"
              borderColor="border-teal-200"
              iconColor="text-teal-600"
              bgHover="hover:bg-teal-50/30"
              chevronBg="bg-teal-100"
              testId={`payment-tracker-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={false}
              summary={(lead.payment_history || []).length > 0 ? `${fmt((lead.payment_history || []).reduce((s, p) => s + (p.amount || 0), 0))} received` : 'No payments'}
              badge={(lead.payment_history || []).length > 0 && (
                <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-bold">
                  {fmt((lead.payment_history || []).reduce((s, p) => s + (p.amount || 0), 0))} received
                </span>
              )}
            >
              <PaymentTracker lead={lead} onDone={onRefresh} />
            </CollapsibleSection>
          )}

          {/* ─── ENGAGEMENT LETTER ─── */}
          {hasMCA && lead.proposal && (
            <CollapsibleSection
              icon={Mail}
              title="Engagement Letter"
              borderColor="border-blue-200"
              iconColor="text-blue-600"
              bgHover="hover:bg-blue-50/30"
              chevronBg="bg-blue-100"
              testId={`engagement-letter-${lead.id}`}
              activeSection={activeSection} setActiveSection={setActiveSection}
              defaultOpen={false}
              summary={lead.engagement_letter_sent ? 'Sent' : 'Not sent'}
              badge={lead.engagement_letter_sent && (
                <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">SENT</span>
              )}
            >
              <EngagementLetterPanel lead={lead} onDone={onRefresh} />
            </CollapsibleSection>
          )}

          {/* ─── RE-CALCULATE SAVINGS CTA ─── */}
          {lead.estimated_savings > 0 && (
            <div className="border-t border-slate-100 px-4 md:px-5 py-3 bg-gradient-to-r from-amber-50/50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    <RefreshCcw className="w-3.5 h-3.5" /> Re-calculate Savings
                  </p>
                  <p className="text-[10px] text-amber-600 mt-0.5">Open the calculator to adjust inputs{hasMCA ? ' based on MCA data' : ''}, then save revised savings back to this lead.</p>
                </div>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-amber-600 hover:bg-amber-700 gap-1.5 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/calculator?recalc_lead=${lead.id}`);
                  }}
                  data-testid={`recalc-btn-${lead.id}`}
                >
                  <RefreshCcw className="w-3 h-3" /> Re-calculate
                </Button>
              </div>
            </div>
          )}

          {/* ─── PUSH TO PARTNER ─── */}
          <div className="border-t border-slate-100 px-4 md:px-5 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5 text-green-600" /> Push to R-PACE Partner
                </p>
                {lead.assigned_partner_name ? (
                  <p className="text-[10px] mt-0.5">
                    <span className={`font-medium ${lead.partner_assignment_status === 'claimed' ? 'text-green-600' : 'text-amber-600'}`}>
                      {lead.partner_assignment_status === 'claimed' ? 'Claimed' : 'Pushed'}
                    </span>
                    <span className="text-slate-400"> — {lead.assigned_partner_name} {lead.assigned_partner_firm ? `(${lead.assigned_partner_firm})` : ''}</span>
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-400 mt-0.5">Assign this lead to a partner in the client's area for follow-up.</p>
                )}
              </div>
              <Button
                size="sm"
                className={`h-8 text-xs gap-1.5 shrink-0 ${lead.assigned_partner_name ? 'bg-slate-500 hover:bg-slate-600' : 'bg-green-600 hover:bg-green-700'}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  setShowPushPartner(!showPushPartner);
                  if (!showPushPartner && partnerSuggestions.length === 0) {
                    try {
                      const token = localStorage.getItem('ratnatraya_token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ccfs-leads/${lead.id}/suggest-partners`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const data = await res.json();
                      setPartnerSuggestions(data.suggestions || []);
                    } catch { toast.error('Failed to load partners'); }
                  }
                }}
                data-testid={`push-partner-btn-${lead.id}`}
              >
                <Send className="w-3 h-3" /> {lead.assigned_partner_name ? 'Reassign' : 'Push to Partner'}
              </Button>
            </div>

            {/* Partner Selection Panel */}
            {showPushPartner && (
              <div className="mt-3 border rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`push-partner-panel-${lead.id}`}>
                {partnerSuggestions.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2 text-center">No active partners found. Approve partner requests first.</p>
                ) : (
                  <>
                    <p className="text-[10px] text-slate-500 font-medium uppercase">Select Partner (sorted by location match)</p>
                    {partnerSuggestions.map(p => (
                      <div
                        key={p.partner_id}
                        onClick={() => setSelectedPartnerId(p.partner_id)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${selectedPartnerId === p.partner_id ? 'border-green-500 bg-green-50 ring-1 ring-green-300' : 'border-slate-200 bg-white hover:border-slate-400'}`}
                        data-testid={`partner-option-${p.partner_id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-[#1e3a5f]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700">{p.name} <span className="text-slate-400">({p.profession})</span></p>
                          <p className="text-[10px] text-slate-400">{p.firm_name}{p.city ? ` — ${p.city}` : ''}{p.state ? `, ${p.state}` : ''}</p>
                        </div>
                        {p.reasons.length > 0 && (
                          <div className="flex items-center gap-1 shrink-0">
                            <MapPin className="w-3 h-3 text-green-500" />
                            <span className="text-[10px] font-medium text-green-600">{p.reasons[0]}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      size="sm"
                      disabled={!selectedPartnerId || pushLoading}
                      className="w-full h-8 text-xs bg-green-600 hover:bg-green-700 gap-1.5 mt-2"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setPushLoading(true);
                        try {
                          const token = localStorage.getItem('ratnatraya_token');
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ccfs-leads/${lead.id}/push-to-partner`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ partner_id: selectedPartnerId }),
                          });
                          const data = await res.json();
                          if (res.ok) {
                            toast.success(data.message);
                            setShowPushPartner(false);
                          } else {
                            toast.error(data.detail || 'Failed to push lead');
                          }
                        } catch { toast.error('Failed to push lead'); }
                        finally { setPushLoading(false); }
                      }}
                      data-testid={`confirm-push-btn-${lead.id}`}
                    >
                      {pushLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Confirm — Push Lead to Partner
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ─── ACTIONS (Convert + Mark Lost + Delete) ─── */}
          <div className="border-t border-slate-100 p-4 md:px-5 md:pb-5">
            {showConvertDialog && (
              <div className="mb-3 bg-slate-50 border border-slate-300 rounded-lg p-3 space-y-2.5" data-testid={`convert-dialog-${lead.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-700">Convert to R-PACE Case — Assign or release into pool</p>
                  <button onClick={() => { setShowConvertDialog(false); setOverrideOriginLock(false); setOverrideReason(''); }} className="text-slate-400 hover:text-slate-600 text-sm leading-none" aria-label="Close">✕</button>
                </div>

                {/* Origin-lock banner — visible whenever lead came from a partner */}
                {originPid && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid={`origin-lock-banner-${lead.id}`}>
                    <Lock className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-amber-800">
                        Partner-originated lead — {originRole} slot locked to {originName}
                      </p>
                      <p className="text-[10px] text-amber-700 mt-0.5">
                        {overrideOriginLock
                          ? 'Override mode ON — you can reassign the locked slot, but a reason and audit trail will be recorded.'
                          : `On conversion, this case's ${originRole} slot will be auto-filled with the originating partner. Commission credit always stays with ${originName}.`}
                      </p>
                      {!overrideOriginLock ? (
                        <button onClick={() => setOverrideOriginLock(true)} className="text-[10px] text-amber-900 underline hover:text-amber-700 mt-1" data-testid={`override-lock-${lead.id}`}>
                          Override lock (requires reason)
                        </button>
                      ) : (
                        <button onClick={() => { setOverrideOriginLock(false); setOverrideReason(''); setConvertCA(''); setConvertCS(''); }} className="text-[10px] text-amber-900 underline hover:text-amber-700 mt-1">
                          Cancel override — re-lock
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wide font-semibold mb-1 flex items-center gap-1">
                      Assign CA (optional)
                      {originRole === 'CA' && !overrideOriginLock && <Lock className="w-2.5 h-2.5 text-amber-600" />}
                    </p>
                    <select
                      value={originRole === 'CA' && !overrideOriginLock ? originPid : convertCA}
                      onChange={e => setConvertCA(e.target.value)}
                      className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white h-8 disabled:bg-slate-100 disabled:cursor-not-allowed"
                      data-testid={`convert-ca-select-${lead.id}`}
                      disabled={activeLoading || (originRole === 'CA' && !overrideOriginLock)}
                    >
                      {originRole === 'CA' && !overrideOriginLock && (
                        <option value={originPid}>{originName} — locked (originating CA)</option>
                      )}
                      {(!originRole || originRole !== 'CA' || overrideOriginLock) && (
                        <option value="">Leave open — any active CA can claim</option>
                      )}
                      {activePartners.filter(p => p.profession === 'CA').map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.firm_name ? ` — ${p.firm_name}` : ''}{p.city ? ` (${p.city})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wide font-semibold mb-1 flex items-center gap-1">
                      Assign CS (optional)
                      {originRole === 'CS' && !overrideOriginLock && <Lock className="w-2.5 h-2.5 text-amber-600" />}
                    </p>
                    <select
                      value={originRole === 'CS' && !overrideOriginLock ? originPid : convertCS}
                      onChange={e => setConvertCS(e.target.value)}
                      className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white h-8 disabled:bg-slate-100 disabled:cursor-not-allowed"
                      data-testid={`convert-cs-select-${lead.id}`}
                      disabled={activeLoading || (originRole === 'CS' && !overrideOriginLock)}
                    >
                      {originRole === 'CS' && !overrideOriginLock && (
                        <option value={originPid}>{originName} — locked (originating CS)</option>
                      )}
                      {(!originRole || originRole !== 'CS' || overrideOriginLock) && (
                        <option value="">Leave open — any active CS can claim</option>
                      )}
                      {activePartners.filter(p => p.profession === 'CS').map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.firm_name ? ` — ${p.firm_name}` : ''}{p.city ? ` (${p.city})` : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Override reason — only when override is ON */}
                {overrideOriginLock && (
                  <div>
                    <p className="text-[9px] text-amber-700 uppercase tracking-wide font-semibold mb-1">Reason for overriding origin-partner lock <span className="text-red-500">*</span></p>
                    <input
                      type="text"
                      placeholder="e.g., Partner unresponsive for 10 days; client requested handover"
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      className="w-full text-xs px-2 py-1.5 rounded border border-amber-300 bg-white h-8"
                      data-testid={`override-reason-${lead.id}`}
                    />
                  </div>
                )}

                <p className="text-[10px] text-slate-500">
                  {(!convertCA && !convertCS && !originPid)
                    ? 'No partner selected — case will be released into the partner pool; first active CA & CS to claim wins.'
                    : (convertCA && convertCS) ? 'Both CA and CS will be assigned directly. Case status: Assigned.'
                    : 'One side will be assigned; the other side stays open for claim from the partner pool.'}
                </p>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowConvertDialog(false); setOverrideOriginLock(false); setOverrideReason(''); }} className="h-7 text-xs border-slate-300">Cancel</Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const payload = { ca_partner_id: convertCA, cs_partner_id: convertCS };
                      if (overrideOriginLock) {
                        payload.force_override = true;
                        payload.override_reason = overrideReason;
                      }
                      setShowConvertDialog(false);
                      setOverrideOriginLock(false);
                      setOverrideReason('');
                      onConvertToCase(payload);
                    }}
                    className="h-7 text-xs bg-[#1e3a5f] hover:bg-[#152d4a] gap-1"
                    disabled={activeLoading || (overrideOriginLock && !overrideReason.trim())}
                    data-testid={`confirm-convert-${lead.id}`}
                  >
                    <ArrowRight className="w-3 h-3" /> Confirm — Create Case
                  </Button>
                </div>
              </div>
            )}
            {showLostDialog && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" data-testid={`lost-dialog-${lead.id}`}>
                <p className="text-xs font-semibold text-red-700">Mark this lead as LOST</p>
                <select value={lostReason} onChange={e => setLostReason(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-red-200 bg-white" data-testid={`lost-reason-${lead.id}`}>
                  <option value="">Select reason...</option>
                  {['Client not interested', 'Pricing too high', 'Competitor won', 'Company already regularised', 'No response after follow-up', 'Not eligible for CCFS', 'Company dissolved / amalgamated', 'Other'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <input type="text" placeholder="Additional notes (optional)" value={lostNotes} onChange={e => setLostNotes(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-red-200 bg-white" data-testid={`lost-notes-${lead.id}`} />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => { setShowLostDialog(false); setLostReason(''); setLostNotes(''); }} className="h-7 text-xs border-slate-300">Cancel</Button>
                  <Button size="sm" disabled={!lostReason} onClick={() => { onMarkLost(lostReason, lostNotes); setShowLostDialog(false); }} className="h-7 text-xs bg-red-600 hover:bg-red-700" data-testid={`confirm-lost-${lead.id}`}>Confirm LOST</Button>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                {pipelineStage !== 'won' && pipelineStage !== 'dropped' && !lead.rpace_case_id && (
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-[#1e3a5f] hover:bg-[#152d4a] gap-1"
                    onClick={async () => {
                      setShowConvertDialog(true);
                      setConvertCA('');
                      setConvertCS('');
                      if (activePartners.length === 0) {
                        setActiveLoading(true);
                        try {
                          const token = localStorage.getItem('ratnatraya_token');
                          const r = await fetch(`${API}/api/admin/partners/active`, { headers: { Authorization: `Bearer ${token}` } });
                          if (r.ok) setActivePartners(await r.json());
                        } catch { /* noop */ }
                        finally { setActiveLoading(false); }
                      }
                    }}
                    data-testid={`convert-case-${lead.id}`}
                  >
                    <ArrowRight className="w-3 h-3" /> Convert to R-PACE Case
                  </Button>
                )}
                {pipelineStage === 'won' && (
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">Case Created</span>
                )}
                {pipelineStage !== 'dropped' && pipelineStage !== 'won' && !lead.rpace_case_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => setShowLostDialog(true)}
                    data-testid={`mark-lost-${lead.id}`}
                  >
                    <X className="w-3 h-3 mr-1" /> Mark as Lost
                  </Button>
                )}
                {lead.rpace_case_id && pipelineStage !== 'won' && (
                  <span className="text-[10px] font-medium text-slate-500 italic" title="Lead has been converted — actions managed in the linked case">
                    Locked — Case open
                  </span>
                )}
                {pipelineStage === 'dropped' && lead.lost_reason && (
                  <span className="text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-full">Lost: {lead.lost_reason}</span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className={`h-7 text-xs ${(lead.rpace_case_id || pipelineStage === 'won') ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                disabled={!!(lead.rpace_case_id || pipelineStage === 'won')}
                title={(lead.rpace_case_id || pipelineStage === 'won') ? 'Cannot delete — this lead has been converted to a case. Manage from Cases.' : 'Delete lead'}
                onClick={() => { if (window.confirm(`Delete lead "${lead.company_name || lead.name}"?`)) onDelete(); }}
                data-testid={`delete-lead-${lead.id}`}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete Lead
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// Named exports for partner read-only views
// All three heavy sections are re-exported from their extracted modules (Phase 2a/2b/2c)
export { BSAnalysisPanel } from '@/pages/admin/ccfs/sections/BSAnalysisPanel';
export { EligibilityEngine } from '@/pages/admin/ccfs/sections/EligibilityEngine';
export { FilingIndexSection } from '@/pages/admin/ccfs/sections/FilingIndexSection';
export { DirectorOtherCompanies } from '@/pages/admin/ccfs/sections/DirectorOtherCompanies';
